<template>
  <v-app :dark="isDarkMode" :class="['inventory-page-bg', { 'dark-mode': isDarkMode }]">
    <!-- Enhanced Loading Modal -->
    <v-overlay
      v-model="showLoadingModal"
      class="loading-overlay"
      :z-index="10000"
      opacity="0.95"
    >
      <div class="loading-modal">
        <div class="loading-content">
          <!-- Loading Animation -->
          <div class="loading-animation">
            <v-progress-circular
              indeterminate
              size="80"
              width="6"
              color="blue"
              class="mb-6"
            ></v-progress-circular>
          </div>

          <!-- Animated Loading Text -->
          <div class="loading-text-container">
            <transition name="fade" mode="out-in">
              <h2 :key="currentLoadingText" class="loading-text">
                {{ currentLoadingText }}
              </h2>
            </transition>
            <p class="loading-subtitle">
              Please wait while we find the perfect vehicles for you...
            </p>
          </div>

          <!-- Loading Progress Dots -->
          <div class="loading-dots">
            <span class="dot" :class="{ active: loadingDotIndex >= 1 }"></span>
            <span class="dot" :class="{ active: loadingDotIndex >= 2 }"></span>
            <span class="dot" :class="{ active: loadingDotIndex >= 3 }"></span>
          </div>

          <!-- Debug buttons for testing (remove in production) -->

        </div>
      </div>
    </v-overlay>

    <!-- Simple loader for normal searches -->
    <v-overlay
      v-model="loader"
      class="simple-loader-overlay"
      :z-index="9999"
      opacity="0.8"
    >
      <v-progress-circular
        indeterminate
        size="64"
        color="primary"
      ></v-progress-circular>
    </v-overlay>

    <v-main class="auth-pages" v-if="company" >

          <!-- Header -->
          <header class="autosyncx-header">
            <!-- Main Navigation Bar -->
            <div class="header-nav">
              <div class="header-nav-content">

                <!-- Mobile: Hamburger -->
                <div class="hamburger-menu" @click="toggleMobileMenu">
                  <v-icon class="hamburger-icon material-icons-round">{{ mobileMenuOpen ? 'close' : 'menu' }}</v-icon>
                </div>

                <!-- Desktop: Nav Links -->
                <nav class="nav-tabs">
                  <div class="nav-tab" :class="{ active: $route.path === '/' || $route.path === '/view-inventory' }" @click="$router.push('/')">HOME</div>

                  <v-menu offset-y open-on-click content-class="cars-dropdown-menu">
                    <template v-slot:activator="{ on, attrs }">
                      <div class="nav-tab nav-tab-dropdown" v-bind="attrs" v-on="on">
                        &#9660;&nbsp;CARS FOR SALE
                      </div>
                    </template>
                    <v-list dense class="cars-dropdown-list">
                      <v-list-item @click="activeCategory = 'all'">
                        <v-list-item-title>All Vehicles</v-list-item-title>
                      </v-list-item>
                      <v-list-item @click="activeCategory = 'available'">
                        <v-list-item-title>Available</v-list-item-title>
                      </v-list-item>
                      <v-list-item @click="activeCategory = 'pending'">
                        <v-list-item-title>Pending</v-list-item-title>
                      </v-list-item>
                      <v-list-item @click="activeCategory = 'sold'">
                        <v-list-item-title>Sold</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>

                  <div class="nav-tab">SPECIALS</div>
                  <div class="nav-tab" @click="$router.push('/reviews')">TESTIMONIALS</div>
                  <div class="nav-tab">FINANCE</div>
                  <div class="nav-tab" :class="{ active: $route.path === '/about-us' }" @click="$router.push('/about-us')">ABOUT US</div>
                  <div class="nav-tab">CONTACT</div>
                </nav>

                <!-- Right: Search + Theme Toggle -->
                <div class="nav-right">
                  <div class="nav-search">
                    <v-icon small color="grey lighten-1">mdi-magnify</v-icon>
                    <input
                      type="text"
                      v-model="searchTerm"
                      placeholder="Search..."
                      class="search-input"
                      @input="onSearchInput"
                    />
                  </div>
                  <div class="theme-toggle">
                    <v-btn icon small @click="toggleDarkMode" class="theme-toggle-btn">
                      <v-icon small class="material-icons-round">{{ isDarkMode ? 'light_mode' : 'dark_mode' }}</v-icon>
                    </v-btn>
                  </div>
                </div>
              </div>

              <!-- Mobile Menu Dropdown -->
              <v-expand-transition>
                <div v-if="mobileMenuOpen" class="mobile-menu-dropdown">
                  <div class="mobile-menu-item" @click="$router.push('/'); mobileMenuOpen = false">
                    <i class="material-icons-round menu-item-icon">home</i>
                    <span>Home</span>
                  </div>
                  <div class="mobile-menu-item" @click="activeCategory = 'available'; mobileMenuOpen = false">
                    <i class="material-icons-round menu-item-icon">directions_car</i>
                    <span>Cars For Sale</span>
                  </div>
                  <div class="mobile-menu-item" @click="$router.push('/reviews'); mobileMenuOpen = false">
                    <i class="material-icons-round menu-item-icon">star</i>
                    <span>Testimonials</span>
                  </div>
                  <div class="mobile-menu-item" @click="$router.push('/about-us'); mobileMenuOpen = false">
                    <i class="material-icons-round menu-item-icon">info</i>
                    <span>About Us</span>
                  </div>
                  <div class="mobile-menu-item" @click="toggleSidebarFilter">
                    <i class="material-icons-round menu-item-icon">filter_list</i>
                    <span>Filters</span>
                  </div>
                  <div class="mobile-menu-item" @click="toggleDarkMode">
                    <i class="material-icons-round menu-item-icon">{{ isDarkMode ? 'light_mode' : 'dark_mode' }}</i>
                    <span>{{ isDarkMode ? 'Light Mode' : 'Dark Mode' }}</span>
                  </div>
                  <div class="mobile-menu-item" @click="callNumber(company ? company.phone_m : '')">
                    <i class="material-icons-round menu-item-icon">call</i>
                    <span>Call Us</span>
                  </div>
                </div>
              </v-expand-transition>
            </div>

            <!-- Inventory Bar with Filters -->
            <div class="header-inventory-bar">
              <div class="inventory-bar-content">
                <!-- Left: Inventory Count -->
                <div class="sidebar-title-section">
                      <span class="sidebar-title">Inventory</span>
                      <span class="sidebar-divider">|</span>
                      <span class="sidebar-count">{{ count_vehicle.toLocaleString() }} Total</span>
                    </div>

                <!-- Center: Filter Tabs -->
                <div class="filter-tabs">
                  <v-btn-toggle v-model="activeCategory" dense class="filter-toggle">
                    <v-btn small text value="all">All</v-btn>
                    <v-btn small text value="available">Available</v-btn>
                    <v-btn small text value="pending">Pending</v-btn>
                    <v-btn small text value="sold">Sold</v-btn>
                  </v-btn-toggle>
                </div>

                <!-- Right: Sort -->
                <div class="inventory-sort">
                  <v-menu offset-y>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        small
                        text
                        class="sort-btn"
                        v-bind="attrs"
                        v-on="on"
                      >
                        <v-icon left small>mdi-sort</v-icon>
                        {{ currentSort.label }}
                        <v-icon right small>mdi-chevron-down</v-icon>
                      </v-btn>
                    </template>
                    <v-list dense>
                      <v-list-item
                        v-for="sort in sortOptions"
                        :key="sort.value"
                        @click="changeSortOrder(sort)"
                      >
                        <v-list-item-title>{{ sort.label }}</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </div>
              </div>
            </div>
          </header>

          <!-- Filter Panel -->
          <v-row style="margin: 0 !important;">
            <v-col cols="12" md="12" style="padding: 0 !important;">
              <!-- Debug: Show filter state -->
              <div v-if="$route.query.debug" style="background: yellow; padding: 10px; text-align: center;">
                DEBUG: showFilters = {{ showFilters }}
              </div>

              <!-- Filter Panel Content -->
                        <section v-if="showFilters || $route.query.debug" class="filter-panel">
                            <v-container>
                              <!-- Debug: Test if container shows -->
                              <div v-if="$route.query.debug" style="background: red; color: white; padding: 5px; margin: 5px;">
                                DEBUG: Inside filter panel container
                              </div>

                              <div class="filter-card" flat>
                                <!-- Debug: Test if card shows -->
                                <div v-if="$route.query.debug" style="background: blue; color: white; padding: 5px; margin: 5px;">
                                  DEBUG: Inside filter card
                                </div>

                                <v-card-text class="filter-content">
                                  <!-- Debug: Test if card-text shows -->
                                 

                                  <v-row>
                                    <!-- Price Range -->
                                    <v-col cols="12" md="3">
                                      <div class="filter-group">
                                        <h4 class="filter-title">Price Range</h4>
                                        <v-row>
                                          <v-col cols="6">
                                            <v-text-field
                                              v-model="filters.minPrice"
                                              label="Min Price"
                                              prefix="$"
                                              type="number"
                                              outlined
                                              dense
                                              hide-details
                                            ></v-text-field>
                                          </v-col>
                                          <v-col cols="6">
                                            <v-text-field
                                              v-model="filters.maxPrice"
                                              label="Max Price"
                                              prefix="$"
                                              type="number"
                                              outlined
                                              dense
                                              hide-details
                                            ></v-text-field>
                                          </v-col>
                                        </v-row>
                                      </div>
                                    </v-col>

                                    <!-- Make -->
                                    <v-col cols="12" md="3">
                                      <div class="filter-group">
                                        <h4 class="filter-title">Make</h4>
                                        <v-select
                                          v-model="filters.make"
                                          :items="makeOptions"
                                          label="Select Make"
                                          outlined
                                          dense
                                          hide-details
                                          clearable
                                        ></v-select>
                                      </div>
                                    </v-col>

                                    <!-- Year Range -->
                                    <v-col cols="12" md="3">
                                      <div class="filter-group">
                                        <h4 class="filter-title">Year</h4>
                                        <v-row>
                                          <v-col cols="6">
                                            <v-text-field
                                              v-model="filters.minYear"
                                              label="Min Year"
                                              type="number"
                                              outlined
                                              dense
                                              hide-details
                                            ></v-text-field>
                                          </v-col>
                                          <v-col cols="6">
                                            <v-text-field
                                              v-model="filters.maxYear"
                                              label="Max Year"
                                              type="number"
                                              outlined
                                              dense
                                              hide-details
                                            ></v-text-field>
                                          </v-col>
                                        </v-row>
                                      </div>
                                    </v-col>

                                    <!-- Mileage Range -->
                                    <v-col cols="12" md="3">
                                      <div class="filter-group">
                                        <h4 class="filter-title">Mileage</h4>
                                        <v-row>
                                          <v-col cols="6">
                                            <v-text-field
                                              v-model="filters.minMileage"
                                              label="Min Miles"
                                              type="number"
                                              outlined
                                              dense
                                              hide-details
                                            ></v-text-field>
                                          </v-col>
                                          <v-col cols="6">
                                            <v-text-field
                                              v-model="filters.maxMileage"
                                              label="Max Miles"
                                              type="number"
                                              outlined
                                              dense
                                              hide-details
                                            ></v-text-field>
                                          </v-col>
                                        </v-row>
                                      </div>
                                    </v-col>
                                  </v-row>

                                  <!-- New Filter Row: Body Style, Drivetrain, Fuel Type, Transmission -->
                                  <v-row class="mt-3">
                                    <!-- Body Style -->
                                    <v-col cols="12" md="3">
                                      <div class="filter-group">
                                        <h4 class="filter-title">Body Style</h4>
                                        <v-select
                                          v-model="filters.bodyStyle"
                                          :items="bodyStyleOptions"
                                          label="Select Body Style"
                                          outlined
                                          dense
                                          hide-details
                                          clearable
                                        ></v-select>
                                      </div>
                                    </v-col>

                                    <!-- Drivetrain -->
                                    <v-col cols="12" md="3">
                                      <div class="filter-group">
                                        <h4 class="filter-title">Drivetrain</h4>
                                        <v-select
                                          v-model="filters.drivetrain"
                                          :items="drivetrainOptions"
                                          label="Select Drivetrain"
                                          outlined
                                          dense
                                          hide-details
                                          clearable
                                        ></v-select>
                                      </div>
                                    </v-col>

                                    <!-- Fuel Type -->
                                    <v-col cols="12" md="3">
                                      <div class="filter-group">
                                        <h4 class="filter-title">Fuel Type</h4>
                                        <v-select
                                          v-model="filters.fuelType"
                                          :items="fuelTypeOptions"
                                          label="Select Fuel Type"
                                          outlined
                                          dense
                                          hide-details
                                          clearable
                                        ></v-select>
                                      </div>
                                    </v-col>

                                    <!-- Transmission -->
                                    <v-col cols="12" md="3">
                                      <div class="filter-group">
                                        <h4 class="filter-title">Transmission</h4>
                                        <v-select
                                          v-model="filters.transmission"
                                          :items="transmissionOptions"
                                          label="Select Transmission"
                                          outlined
                                          dense
                                          hide-details
                                          clearable
                                        ></v-select>
                                      </div>
                                    </v-col>
                                  </v-row>

                                  <!-- Filter Actions -->
                                  <v-row class="mt-4">
                                    <v-col cols="12" class="text-center">
                                      <v-btn
                                        color="blue"
                                        dark
                                        @click="applyFilters"
                                        class="mr-3"
                                      >
                                        <v-icon left class="material-icons-round">search</v-icon>
                                        Apply Filters
                                      </v-btn>
                                      <v-btn
                                        outlined
                                        @click="clearFilters"
                                      >
                                        <v-icon left class="material-icons-round">clear</v-icon>
                                        Clear All
                                      </v-btn>
                                    </v-col>
                                  </v-row>
                                </v-card-text>
                              </div>
                            </v-container>
                          </section>
            </v-col>
          </v-row>

          <!-- AI Search Notifications -->
          <v-container v-if="isAiSearch">
            <!-- External search in progress notification - Only for AI searches -->
            <v-alert
              v-if="searching_external"
              dense
              text
              color="primary"
              class="mt-2 ai-search-alert"
            >
              <div class="d-flex align-center">
                <v-progress-circular
                  indeterminate
                  size="24"
                  width="3"
                  color="primary"
                  class="mr-3"
                ></v-progress-circular>
                <div>
                  <div class="font-weight-bold">SyncGuideX™ in Progress</div>
                  <div class="text-caption">
                    <span v-if="!hasExternalResults">SyncGuideX™ is searching the web for the best vehicle matches based on your criteria...</span>
                    <span v-else>Found {{ externalVehicleCount }} vehicles so far... still searching for more matches...</span>
                  </div>
                </div>
              </div>
            </v-alert>

            <!-- External search results notification - Only for AI searches -->
            <v-alert
              v-if="hasExternalResults && !searching_external"
              dense
              text
              color="success"
              class="mt-2 ai-results-alert"
            >
              <div class="d-flex align-center">
                <v-icon color="green" class="mr-3 material-icons-round">check_circle</v-icon>
                <div>
                  <div class="font-weight-bold">SyncGuideX™ Search Complete</div>
                  <div class="text-caption">We found {{ externalVehicleCount }} additional vehicles from external sources. Click on "View" to see full details on the original website.</div>
                </div>
              </div>
            </v-alert>
          </v-container>

          <!-- Vehicles Section with Left Sidebar -->
          <section class="vehicles-section">
            <v-container fluid class="pa-0">
              <div class="inventory-layout">
                <!-- Left Sidebar Filter Panel -->
                <aside class="sidebar-filter-panel">
                  <!-- Header with Tabs -->
                  <div class="sidebar-header">
                    <!-- <div class="sidebar-title-section">
                      <span class="sidebar-title">Inventory</span>
                      <span class="sidebar-divider">|</span>
                      <span class="sidebar-count">{{ count_vehicle.toLocaleString() }} Total</span>
                    </div> -->
                    <!-- Category Tabs -->
                    <div class="category-tabs">
                      <button
                        v-for="tab in categoryTabs"
                        :key="tab.value"
                        class="category-tab"
                        :class="{ active: activeCategory === tab.value }"
                        @click="setActiveCategory(tab.value)"
                      >
                        <v-icon small class="tab-icon">{{ tab.icon }}</v-icon>
                        <span class="tab-label">{{ tab.label }}</span>
                        <span v-if="tab.count !== undefined" class="tab-count">({{ tab.count }})</span>
                      </button>
                    </div>
                  </div>

                  <!-- Filter Section -->
                  <div class="sidebar-filter-section">
                    <div class="filter-header" @click="sidebarFilterExpanded = !sidebarFilterExpanded">
                      <span class="filter-label">
                        FILTER
                        <span v-if="hasSidebarFiltersActive" class="filter-badge">{{ sidebarFiltersCount }}</span>
                      </span>
                      <v-icon small class="filter-toggle-icon material-icons-round">{{ sidebarFilterExpanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down' }}</v-icon>
                    </div>

                    <v-expand-transition>
                      <div v-show="sidebarFilterExpanded" class="filter-body">
                        <!-- Type Filter -->
                        <div class="filter-group-sidebar">
                          <div class="filter-group-header">
                            <span class="filter-group-title">Type</span>
                            <span class="filter-group-count">({{ filteredUsers.length }})</span>
                          </div>
                          <div class="filter-options">
                            <label class="filter-option" :class="{ active: !sidebarFilters.selectedTypes.length }">
                              <input type="checkbox" :checked="!sidebarFilters.selectedTypes.length" @change="clearTypeFilter" />
                              <span class="option-label">All</span>
                              <span class="option-count">({{ apiTotalAvailable || (reps_users ? reps_users.length : 0) }})</span>
                            </label>
                            <label v-for="type in vehicleTypeOptions" :key="type.value" class="filter-option" :class="{ active: sidebarFilters.selectedTypes.includes(type.value) }">
                              <input type="checkbox" :value="type.value" v-model="sidebarFilters.selectedTypes" />
                              <span class="option-label">{{ type.label }}</span>
                              <span class="option-count">{{ type.count }}</span>
                            </label>
                          </div>
                        </div>

                        <!-- Price Filter -->
                        <div class="filter-group-sidebar">
                          <div class="filter-group-header collapsible" @click="priceFilterExpanded = !priceFilterExpanded">
                            <span class="filter-group-title">Price</span>
                            <v-icon small>{{ priceFilterExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                          </div>
                          <v-expand-transition>
                            <div v-show="priceFilterExpanded" class="filter-slider-container">
                              <v-range-slider
                                v-model="sidebarFilters.priceRange"
                                :min="5000"
                                :max="50000"
                                :step="500"
                                hide-details
                                color="blue"
                                track-color="grey lighten-2"
                                class="price-slider"
                              ></v-range-slider>
                              <div class="slider-labels">
                                <span class="slider-min">${{ sidebarFilters.priceRange[0].toLocaleString() }}+</span>
                                <span class="slider-max">${{ sidebarFilters.priceRange[1].toLocaleString() }}</span>
                              </div>
                            </div>
                          </v-expand-transition>
                        </div>

                        <!-- Mileage Filter -->
                        <div class="filter-group-sidebar">
                          <div class="filter-group-header collapsible" @click="mileageFilterExpanded = !mileageFilterExpanded">
                            <span class="filter-group-title">Mileage</span>
                            <span class="filter-range-label">{{ sidebarFilters.mileageRange[0].toLocaleString() }}- {{ sidebarFilters.mileageRange[1].toLocaleString() }}+ Miles</span>
                          </div>
                          <v-expand-transition>
                            <div v-show="mileageFilterExpanded" class="filter-slider-container">
                              <v-range-slider
                                v-model="sidebarFilters.mileageRange"
                                :min="0"
                                :max="100000"
                                :step="1000"
                                hide-details
                                color="blue"
                                track-color="grey lighten-2"
                                class="mileage-slider"
                              ></v-range-slider>
                              <div class="slider-labels">
                                <span class="slider-min">{{ sidebarFilters.mileageRange[0].toLocaleString() }}+</span>
                                <span class="slider-max">{{ sidebarFilters.mileageRange[1].toLocaleString() }}+</span>
                              </div>
                            </div>
                          </v-expand-transition>
                        </div>

                        <!-- Filter Actions -->
                        <div class="sidebar-filter-actions">
                          <v-btn color="blue" dark small class="search-btn" @click="applySidebarFilters">
                            SEARCH
                          </v-btn>
                          <v-btn outlined small class="clear-btn" @click="clearSidebarFilters">
                            CLEAR FILTERS
                          </v-btn>
                        </div>
                      </div>
                    </v-expand-transition>
                  </div>
                </aside>

                <!-- Main Content Area -->
                <main class="main-content-area">
                  <!-- Comparison view for similar vehicles -->
                  <vehicle-comparison-card
                    v-if="isComparisonMode && filteredUsers.length > 0"
                    :vehicles="filteredUsers"
                    :dark-mode="isDarkMode"
                    @vehicle-viewed="handleVehicleViewed"
                  />

                  <!-- Regular vehicle cards -->
                  <div v-else-if="filteredUsers.length > 0" class="vehicles-grid">
                    <TrueCarVehicleCard
                      v-for="vehicle in filteredUsers"
                      :key="vehicle.id"
                      :vehicle="vehicle"
                      :dark-mode="isDarkMode"
                      @vehicle-clicked="handleVehicleViewed"
                      @contact-dealer="handleVehicleDetailRequested"
                      @lead-required="handleLeadRequired"
                    />
                  </div>

                  <!-- Lazy load sentinel & spinner (no-domain / all-vehicles mode) -->
                  <div ref="lazySentinel" class="lazy-sentinel">
                    <div v-if="isLoadingMore" class="text-center py-6">
                      <v-progress-circular indeterminate color="primary" size="36"></v-progress-circular>
                      <p class="mt-3 grey--text text-body-2">Loading more vehicles...</p>
                    </div>
                  </div>

                  <!-- No vehicles message -->
                  <div v-if="!loader && filteredUsers.length === 0" class="empty-state">
                    <v-card class="empty-card mx-auto" elevation="0">
                      <v-card-text class="text-center pa-12">
                        <div class="empty-icon mb-6">
                          <v-icon size="120" color="grey lighten-2" class="material-icons-round">
                            directions_car
                          </v-icon>
                        </div>
                        <h3 class="empty-title mb-4">No Vehicles Available</h3>
                        <p class="empty-subtitle mb-6">
                          We're currently updating our inventory with new arrivals.<br>
                          Please check back soon for the latest vehicles.
                        </p>
                        <v-btn
                          color="primary"
                          large
                          rounded
                          @click="getInventory(getSubdomain())"
                          class="refresh-btn"
                        >
                          <v-icon left class="material-icons-round">refresh</v-icon>
                          Refresh Inventory
                        </v-btn>
                      </v-card-text>
                    </v-card>
                  </div>

                  <!-- Debug info -->
                  <div v-if="$route.query.debug" class="debug-info mt-4 pa-4" style="background: #f5f5f5; border: 1px solid #ddd;">
                    <h3>Debug Info:</h3>
                    <p><strong>reps_users length:</strong> {{ reps_users ? reps_users.length : 'null/undefined' }}</p>
                    <p><strong>filteredUsers length:</strong> {{ filteredUsers ? filteredUsers.length : 'null/undefined' }}</p>
                    <p><strong>searchTerm:</strong> "{{ searchTerm }}"</p>
                    <p><strong>isComparisonMode:</strong> {{ isComparisonMode }}</p>
                    <details v-if="reps_users && reps_users.length > 0">
                      <summary>Raw reps_users data (first 2 items):</summary>
                      <pre>{{ JSON.stringify(reps_users.slice(0, 2), null, 2) }}</pre>
                    </details>
                  </div>
                </main>
              </div>
            </v-container>
          </section>

          <!-- Footer Section -->
          <section class="footer-section">
            <v-container>
              <div class="text-center py-4">
                <h1 class="text-h6">&copy; Herrera Auto Sales &nbsp;|&nbsp; Powered by <strong>AutoSyncX&trade;</strong></h1>
              </div>
            </v-container>
          </section>

          <v-dialog
                      v-model="overlay"
                      width="500"
                    >
                    <img v-if="company"  class="" :src="base_url_img+'vcard/'+company.user_id+'-QR.png'" width="50">


                  </v-dialog>

                  <!-- SyncGuideX Chat Component -->
                  <sync-guide-x-chat
                    v-model="showSyncGuideX"
                    @search="handleSyncGuideXSearch"
                  />

          <!-- Mobile Bottom Navigation -->
          <div class="mobile-bottom-nav" :class="{ 'dark-mode': isDarkMode }">
            <div class="bottom-nav-container">
              <div class="bottom-nav-item" @click="goToCard">
                <i class="material-icons-round bottom-nav-icon">contact_page</i>
                <span class="bottom-nav-label">Card</span>
              </div>
              <div class="bottom-nav-item" @click.stop="overlay = !overlay">
                <i class="material-icons-round bottom-nav-icon">qr_code_2</i>
                <span class="bottom-nav-label">Scan</span>
              </div>
              <div class="bottom-nav-item active">
                <i class="material-icons-round bottom-nav-icon">directions_car</i>
                <span class="bottom-nav-label">Inventory</span>
              </div>
              <div class="bottom-nav-item" @click="callNumber(company ? company.phone_m : '')">
                <i class="material-icons-round bottom-nav-icon">call</i>
                <span class="bottom-nav-label">Call</span>
              </div>
            </div>
          </div>

    </v-main>

    <!-- Digital Assistant Chat -->
    <!-- <DigitalAssistantChat
      v-if="currentDomain && company"
      :domain="currentDomain"
      :user-id="company.user_id"
      :user-name="company.first_name"
      :user-avatar="base_url_img + company.avatar"
      :auto-start="true"
      ref="digitalAssistant"
    /> -->
  </v-app>
</template>

<style scoped>
/* =============================================
   Full Page Background
   ============================================= */
.inventory-page-bg {
  position: relative;
  min-height: 100vh;
  overflow: hidden;
}

.inventory-page-bg::before {
  content: '';
  position: fixed;
  inset: 0;
  background-image: url('~@/assets/img/bg-inventory.jpeg');
  background-size: cover;
  background-position: center top;
  background-repeat: no-repeat;
  filter: blur(1px);
  transform: scale(1.05);
  z-index: 0;
}

.inventory-page-bg > * {
  position: relative;
  z-index: 1;
}

/* =============================================
   Professional Header with Skyline Design
   ============================================= */
.autosyncx-header {
  width: 100%;
  position: sticky;
  top: 0;
  z-index: 100;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
}

/* Skyline Banner */
.header-skyline {
  background: linear-gradient(to bottom, #d8dce3 0%, #c5c9d1 100%);
  background-image: url('~@/assets/img/skyline-bg.png');
  background-size: cover;
  background-position: center bottom;
  padding: 20px 30px;
  position: relative;
}

.header-skyline-content {
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  max-width: 1400px;
  margin: 0 auto;
}

/* Hamburger in skyline header - positioned absolute left */
.header-skyline .hamburger-menu {
  position: absolute;
  left: 0px;
  top: 50%;
  transform: translateY(-50%);
}

/* Logo */
.header-logo {
  display: flex;
  align-items: center;
}

.logo-image {
  height: 60px;
  width: auto;
  object-fit: contain;
}

.hamburger-menu {
  display: flex;
  align-items: center;
  cursor: pointer;
}

/* Company Info - Right Side */
.header-company {
  position: absolute;
  right: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #4a5568;
  font-size: 0.9rem;
}

.company-name {
  font-weight: 500;
}

/* Navigation Bar */
.header-nav {
  background: #ffffff;
  padding: 0 30px;
  min-height: 64px;
  border-bottom: 2px solid #e2e8f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.header-nav-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1400px;
  margin: 0 auto;
  min-height: 64px;
  gap: 16px;
}

.nav-left {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.nav-right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.nav-tabs {
  display: flex;
  align-items: center;
}

.nav-tab {
  display: flex;
  align-items: center;
  height: 64px;
  padding: 0 18px;
  color: #1a202c;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  cursor: pointer;
  border-bottom: 3px solid transparent;
  transition: all 0.2s ease;
  white-space: nowrap;
  user-select: none;
}

.nav-tab:hover {
  color: #3d4a5c;
  border-bottom-color: #3d4a5c;
  background: rgba(61, 74, 92, 0.05);
}

.nav-tab.active {
  color: #3d4a5c;
  border-bottom-color: #3d4a5c;
  background: rgba(61, 74, 92, 0.07);
}

.nav-tab-dropdown {
  position: relative;
}

/* Search in Nav */
.nav-search {
  display: flex;
  align-items: center;
  background: #f7f8fa;
  border-radius: 6px;
  padding: 7px 14px;
  min-width: 200px;
  border: 1px solid #e2e8f0;
}

.nav-search .search-input {
  background: transparent;
  border: none;
  outline: none;
  color: #2d3748;
  font-size: 0.9rem;
  margin-left: 8px;
  width: 100%;
}

.nav-search .search-input::placeholder {
  color: #a0aec0;
}

/* Ticker Banner */
.header-ticker {
  background: #ffffff;
  border-bottom: 1px solid #e2e8f0;
  padding: 9px 30px;
  text-align: center;
}

.ticker-text {
  font-size: 0.875rem;
  color: #4a5568;
  font-style: italic;
  letter-spacing: 0.2px;
}

/* Cars For Sale Dropdown */
.cars-dropdown-menu {
  border-radius: 6px !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12) !important;
}

.cars-dropdown-list .v-list-item {
  font-size: 0.8rem !important;
  font-weight: 700 !important;
  letter-spacing: 0.5px !important;
  text-transform: uppercase !important;
  color: #1a202c !important;
}

.cars-dropdown-list .v-list-item:hover {
  background: rgba(61, 74, 92, 0.07) !important;
  color: #3d4a5c !important;
}

/* Inventory Bar */
.header-inventory-bar {
  background: rgba(255, 255, 255, 0.95);
  padding: 14px 30px;
  border-bottom: 1px solid #e2e8f0;
  min-height: 56px;
}

.inventory-bar-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1400px;
  margin: 0 auto;
  min-height: 40px;
}

.inventory-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.inventory-title {
  font-size: 1.2rem;
  font-weight: 600;
  color: #2d3748;
}

.inventory-divider {
  color: #cbd5e0;
  font-size: 1.2rem;
}

.inventory-count {
  font-size: 1.1rem;
  color: #718096;
  font-weight: 500;
}

/* Filter Tabs */
.filter-tabs {
  display: flex;
  align-items: center;
}

.filter-toggle {
  background: transparent !important;
}

.filter-toggle .v-btn {
  text-transform: none !important;
  font-weight: 600 !important;
  color: #718096 !important;
  border: none !important;
  font-size: 0.9rem !important;
  padding: 0 16px !important;
}

.filter-toggle .v-btn--active {
  color: #5b6abf !important;
  background: rgba(91, 106, 191, 0.1) !important;
}

/* Sort Button */
.sort-btn {
  text-transform: none !important;
  color: #718096 !important;
  font-size: 0.9rem !important;
  font-weight: 500 !important;
}

/* Hamburger Menu - Hidden by default on desktop */
.hamburger-menu {
  display: none;
  cursor: pointer;
  padding: 0px;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.hamburger-menu:hover {
  background: rgba(0, 0, 0, 0.1);
}

.hamburger-icon {
  color: #2d3748 !important;
  font-size: 28px !important;
}

/* Dark mode hamburger */
.dark-mode .hamburger-icon {
  color: #ffffff !important;
}

.dark-mode .hamburger-menu:hover {
  background: rgba(255, 255, 255, 0.1);
}

/* Mobile Menu Dropdown */
.mobile-menu-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(245, 245, 250, 0.98) 100%);
  backdrop-filter: blur(20px);
  padding: 10px 0;
  z-index: 1000;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  border-top: 1px solid rgba(0, 0, 0, 0.05);
}

.mobile-menu-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 20px;
  color: #374151;
  cursor: pointer;
  transition: all 0.3s ease;
}

.mobile-menu-item:hover {
  background: rgba(59, 130, 246, 0.1);
}

.menu-item-icon {
  color: #6366f1 !important;
  font-size: 22px !important;
}

.mobile-menu-item span {
  font-size: 0.95rem;
  font-weight: 500;
}

/* Dark mode mobile menu */
.dark-mode .mobile-menu-dropdown {
  background: linear-gradient(180deg, rgba(30, 30, 46, 0.98) 0%, rgba(26, 26, 40, 0.98) 100%);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.dark-mode .mobile-menu-item {
  color: #ffffff;
}

.dark-mode .menu-item-icon {
  color: #a78bfa !important;
}

.dark-mode .mobile-menu-item:hover {
  background: rgba(124, 92, 255, 0.15);
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .header-nav {
    min-height: 60px;
    padding: 0 12px;
  }

  .header-nav-content {
    min-height: 60px;
  }

  /* Hide tabs and search on mobile — hamburger handles navigation */
  .nav-tabs {
    display: none;
  }

  .nav-search {
    display: none;
  }

  /* Logo smaller on mobile */
  .logo-image {
    height: 44px;
  }

  .header-company {
    display: none;
  }

  .header-nav {
    padding: 0 15px;
    position: relative;
  }

  .hamburger-menu {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .nav-search {
    min-width: 140px;
  }

  .header-inventory-bar {
    padding: 10px 15px;
  }

  .inventory-bar-content {
    flex-wrap: wrap;
    gap: 10px;
  }

  .filter-tabs {
    order: 3;
    width: 100%;
    overflow-x: auto;
  }
}

@media (max-width: 480px) {
  .logo-image {
    height: 36px;
  }

  .inventory-info {
    width: 100%;
  }

  .inventory-sort {
    width: 100%;
  }
}

/* =============================================
   Legacy Search Header Styles (kept for reference)
   ============================================= */
.search-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 2rem 0;
  margin-bottom: 2rem;
}

.search-content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.search-title-section {
  text-align: center;
}

.search-title {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  text-shadow: 0 2px 4px rgba(0,0,0,0.3);
}

.search-subtitle {
  font-size: 1.1rem;
  opacity: 0.9;
  margin: 0;
}

.search-filter-bar {
  display: flex;
  gap: 1rem;
  align-items: center;
  max-width: 800px;
  margin: 0 auto;
  width: 100%;
}

.search-input-section {
  flex: 1;
}

.search-input {
  background: white;
  border-radius: 8px;
}

.search-input >>> .v-input__control {
  min-height: 35px;
}

.search-input >>> .v-input__slot {
  margin-bottom: 0 !important;
}

.search-input >>> .v-text-field__details {
  display: none !important;
}

.search-input >>> .v-input__append-inner,
.search-input >>> .v-input__prepend-inner {
  margin-top: 0 !important;
  margin-bottom: 0 !important;
}

.search-input >>> .v-input {
  margin-bottom: 0 !important;
}

.search-input >>> .v-text-field {
  margin-bottom: 0 !important;
  padding-bottom: 0 !important;
}

.filter-buttons {
  display: flex;
  gap: 0.5rem;
}

.filter-btn {
  height: 48px;
  border-radius: 8px;
  font-weight: 600;
  text-transform: none;
  background: rgba(255,255,255,0.1);
  border-color: rgba(255,255,255,0.3) !important;
  color: white !important;
}

.filter-btn:hover {
  background: rgba(255,255,255,0.2);
}

.ai-search-btn {
  height: 48px;
  border-radius: 8px;
  font-weight: 600;
  text-transform: none;
}

/* Make Vuetify's root wrapper transparent so ::before blur shows through */
::v-deep .v-application--wrap {
  background: transparent !important;
}

/* Vuetify dark theme sets background on .v-application itself — override it */
::v-deep .v-application {
  background: transparent !important;
}

::v-deep .theme--dark.v-application {
  background: transparent !important;
}

::v-deep .theme--light.v-application {
  background: transparent !important;
}

/* Vehicles Section */
.vehicles-section {
  padding: 0;
  background: transparent;
  min-height: calc(100vh - 200px);
}

.dark-mode .vehicles-section {
  background: transparent !important;
}

/* Inventory Layout - Sidebar + Main Content */
.inventory-layout {
  display: flex;
  gap: 0;
  min-height: calc(100vh - 200px);
}

/* Left Sidebar Filter Panel */
.sidebar-filter-panel {
  width: 280px;
  min-width: 280px;
  background: rgba(255, 255, 255, 0.82);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  border-right: 1px solid #e0e4e8;
  box-shadow: 2px 0 15px rgba(0, 0, 0, 0.05);
  height: fit-content;
  position: sticky;
  top: 120px;
}

.sidebar-header {
  padding: 15px 20px;
  border-bottom: 1px solid #e8eaec;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.sidebar-title-section {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.sidebar-title {
  font-size: 1.3rem;
  font-weight: 700;
  color: #0f172a;
  text-shadow: 0 1px 3px rgba(255,255,255,0.6);
}

.sidebar-divider {
  color: #c0c4c8;
  font-weight: 300;
}

.sidebar-count {
  font-size: 1rem;
  color: #0f172a;
  font-weight: 600;
}

/* Category Tabs */
.category-tabs {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.category-tab {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border: 1px solid #d0d4d8;
  border-radius: 6px;
  background: #ffffff;
  color: #6a7a8a;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.category-tab:hover {
  background: #f5f7fa;
  border-color: #b0b4b8;
}

.category-tab.active {
  background: linear-gradient(180deg, #6b7a9a 0%, #4a5a7a 100%);
  border-color: #4a5a7a;
  color: #ffffff;
}

.category-tab .tab-icon {
  font-size: 14px !important;
}

.category-tab .tab-label {
  text-transform: capitalize;
}

.category-tab .tab-count {
  font-size: 0.65rem;
  color: #374151;
  font-weight: 600;
  margin-left: 2px;
}

.sidebar-filter-section {
  padding: 0;
}

.filter-header {
  padding: 15px 20px;
  background: transparent;
  border-bottom: 1px solid rgba(0,0,0,0.12);
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  transition: background 0.2s;
}

.filter-header:hover {
  background: rgba(255,255,255,0.2);
}

.filter-label {
  font-size: 0.85rem;
  font-weight: 700;
  color: #0f172a;
  text-shadow: 0 1px 3px rgba(255,255,255,0.6);
  letter-spacing: 1px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter-badge {
  background: #3b82f6;
  color: #ffffff;
  font-size: 0.65rem;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 10px;
  min-width: 18px;
  text-align: center;
}

.dark-mode .filter-badge {
  background: #7c5cff;
}

.filter-toggle-icon {
  color: #0f172a;
}

.filter-body {
  padding: 15px 20px;
}

.filter-group-sidebar {
  margin-bottom: 20px;
}

.filter-group-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.filter-group-header.collapsible {
  cursor: pointer;
  padding: 8px 0;
  border-bottom: 1px solid rgba(0,0,0,0.15);
}

.filter-group-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #0f172a;
  text-shadow: 0 1px 3px rgba(255,255,255,0.6);
}

.filter-group-count {
  font-size: 0.85rem;
  color: #1e293b;
  font-weight: 600;
}

.filter-range-label {
  font-size: 0.75rem;
  color: #1e293b;
  font-weight: 600;
}

.filter-options {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-option {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.2s;
}

.filter-option:hover {
  background: #f5f7fa;
}

.filter-option.active {
  background: #e8f0fe;
}

.filter-option input[type="checkbox"] {
  width: 18px;
  height: 18px;
  accent-color: #4a90d9;
}

.option-label {
  flex: 1;
  font-size: 0.9rem;
  color: #4a5a6a;
}

.option-count {
  font-size: 0.85rem;
  color: #4a90d9;
  font-weight: 500;
}

.filter-slider-container {
  padding: 15px 5px;
}

.slider-labels {
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
  font-size: 0.8rem;
  font-weight: 600;
  color: #0f172a;
}

.sidebar-filter-actions {
  display: flex;
  gap: 8px;
  margin-top: 20px;
  padding-top: 15px;
  border-top: 1px solid #e8eaec;
}

.search-btn {
  flex: 0 0 auto;
  min-width: 90px !important;
  height: 36px !important;
  text-transform: uppercase;
  font-weight: 600;
  font-size: 0.8rem !important;
  letter-spacing: 0.5px;
  border-radius: 6px !important;
  background: linear-gradient(180deg, #6b7a9a 0%, #4a5a7a 100%) !important;
  box-shadow: 0 2px 4px rgba(74, 90, 122, 0.3) !important;
}

.search-btn:hover {
  background: linear-gradient(180deg, #7b8aaa 0%, #5a6a8a 100%) !important;
}

.clear-btn {
  flex: 1;
  height: 36px !important;
  text-transform: uppercase;
  font-size: 0.7rem !important;
  font-weight: 500;
  letter-spacing: 0.3px;
  border-radius: 6px !important;
  background: #ffffff !important;
  border: 1px solid #d0d4d8 !important;
  color: #5a6a7a !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08) !important;
}

.clear-btn:hover {
  background: #f5f7fa !important;
  border-color: #c0c4c8 !important;
}

/* Main Content Area */
.main-content-area {
  flex: 1;
  padding: 20px 30px;
  overflow-y: auto;
}

.vehicles-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1.5rem;
}

/* Empty State */
.empty-state {
  margin: 4rem 0;
}

.empty-card {
  max-width: 600px;
  border-radius: 16px;
  border: 2px dashed #e0e0e0;
}

.empty-icon {
  opacity: 0.6;
}

.empty-title {
  color: #424242;
  font-weight: 600;
}

.empty-subtitle {
  color: #757575;
  line-height: 1.6;
}

.refresh-btn {
  text-transform: none;
  font-weight: 600;
}

/* Mobile Responsive */
@media (max-width: 960px) {
  .inventory-layout {
    flex-direction: column;
  }

  .sidebar-filter-panel {
    width: 100%;
    min-width: 100%;
    position: relative;
    top: 0;
    border-right: none;
    border-bottom: 1px solid #e0e4e8;
  }

  .main-content-area {
    padding: 15px;
  }

  .vehicles-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
}

@media (max-width: 768px) {
  .search-title {
    font-size: 2rem;
  }

  .search-filter-bar {
    flex-direction: column;
    gap: 1rem;
  }

  .search-input-section {
    width: 100%;
  }

  .filter-buttons {
    width: 100%;
    justify-content: center;
  }

  .ai-search-btn {
    flex: 1;
    max-width: 200px;
  }

  .sidebar-header {
    padding: 15px;
  }

  .sidebar-title {
    font-size: 1.2rem;
  }

  .filter-body {
    padding: 10px 15px;
  }

  .sidebar-filter-actions {
    flex-direction: column;
  }
}

/* Filter Panel Styles */
.filter-panel {
  background: #f8f9fa !important;
  border-top: 1px solid #e0e0e0;
  padding: 1.5rem 0;
  width: 100%;
  position: relative;
  z-index: 1;
  min-height: 200px; /* Debug: ensure panel has height */
}

.filter-card {
  background: white !important;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  width: 100%;
  min-height: 150px; /* Debug: ensure card has height */
}

.filter-content {
  padding: 1.5rem !important;
  background: white !important;
  position: relative;
  z-index: 2;
}

.filter-group {
  margin-bottom: 1rem;
  background: rgba(255,255,255,0.9);
  padding: 10px;
  border-radius: 8px;
}

.filter-title {
  color: #424242 !important;
  font-weight: 600;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: block !important;
  visibility: visible !important;
}

/* Filter input styling */
.filter-group >>> .v-input {
  font-size: 0.9rem;
}

.filter-group >>> .v-input__control {
  min-height: 40px;
}

.filter-group >>> .v-text-field__details {
  display: none;
}

/* Filter actions */
.filter-actions {
  border-top: 1px solid #e0e0e0;
  padding-top: 1rem;
  margin-top: 1rem;
}

/* Theme Toggle Button */
.theme-toggle {
  margin-left: 16px;
}

.about-nav-link {
  font-size: 0.9rem;
  font-weight: 600;
  color: #f1f5f9;
  text-decoration: none;
  padding: 6px 14px;
  border-radius: 8px;
  border: 1px solid rgba(255,255,255,0.3);
  background: rgba(255,255,255,0.12);
  transition: background 0.2s;
}
.about-nav-link:hover {
  background: rgba(255,255,255,0.25);
}
@media (max-width: 768px) {
  .about-nav-link {
    display: none;
  }
}

.theme-toggle-btn {
  color: #718096 !important;
  transition: all 0.3s ease;
}

.theme-toggle-btn:hover {
  color: #3d4a5c !important;
  background: rgba(61, 74, 92, 0.08) !important;
}

/* ==========================================
   DARK MODE STYLES - Purple Theme
   ========================================== */

.dark-mode {
  --dark-bg: #0f0a1f;
  --dark-primary: #1a1230;
  --dark-secondary: #251a45;
  --dark-accent: #7c5cff;
  --dark-text: #ffffff;
  --dark-text-secondary: #e0e0e0;
  --dark-text-muted: #b0a8c9;
  --dark-border: rgba(124, 92, 255, 0.4);
  --dark-card-bg: #1e1535;
  --dark-hover: rgba(124, 92, 255, 0.15);
  --dark-success: #10b981;
}

/* Dark Mode - Main Background */
.dark-mode.inventory-page-bg {
  background: linear-gradient(180deg, #0f0a1f 0%, #1a1230 50%, #0f0a1f 100%) !important;
  min-height: 100vh;
}

/* Dark Mode - Make background image more transparent and less blurred */
.dark-mode.inventory-page-bg::before {
  opacity: 1;
  filter: blur(2px);
}

.dark-mode .vehicles-section {
  background-image: none !important;
  background-color: transparent !important;
}

/* Dark Mode - Header Skyline */
.dark-mode .header-skyline {
  background-image: url('~@/assets/img/bg-inventory.jpeg') !important;
  background-size: cover !important;
  background-position: center bottom !important;
}

/* Dark Mode - ALL TEXT WHITE */
.dark-mode,
.dark-mode * {
  color: #ffffff;
}

.dark-mode .v-application,
.dark-mode .v-application *:not(.v-icon):not(.material-icons-round) {
  color: #ffffff !important;
}

/* Dark Mode - Navigation Bar */
.dark-mode .header-nav {
  background: #1a1230 !important;
  border-bottom: 1px solid var(--dark-border) !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4) !important;
}

/* Dark Mode - Ticker */
.dark-mode .header-ticker {
  background: #1a1230 !important;
  border-bottom: 1px solid var(--dark-border) !important;
}

.dark-mode .ticker-text {
  color: #a0aec0 !important;
}

/* Dark Mode - Dropdown */
.dark-mode .cars-dropdown-menu {
  background: #1e1535 !important;
}

.dark-mode .cars-dropdown-list .v-list-item {
  color: #ffffff !important;
}

.dark-mode .cars-dropdown-list .v-list-item:hover {
  background: rgba(124, 92, 255, 0.2) !important;
  color: #ffffff !important;
}

.dark-mode .nav-tab {
  color: #ffffff !important;
  font-weight: 500 !important;
}

.dark-mode .nav-tab:hover,
.dark-mode .nav-tab.active {
  color: #ffffff !important;
  background: var(--dark-accent) !important;
  border-bottom-color: var(--dark-accent) !important;
}

.dark-mode .nav-search {
  background: rgba(255, 255, 255, 0.1) !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
}

.dark-mode .search-input {
  color: #ffffff !important;
}

.dark-mode .search-input::placeholder {
  color: rgba(255, 255, 255, 0.6) !important;
}

.dark-mode .theme-toggle-btn {
  color: #fbbf24 !important;
}

.dark-mode .theme-toggle-btn:hover {
  color: #ffffff !important;
  background: rgba(255, 255, 255, 0.1) !important;
}

/* Dark Mode - Inventory Bar */
.dark-mode .header-inventory-bar {
  background: #1a1230 !important;
  border-bottom: 1px solid var(--dark-border) !important;
}

.dark-mode .inventory-title,
.dark-mode .inventory-count {
  color: #ffffff !important;
}

.dark-mode .inventory-divider {
  color: rgba(255, 255, 255, 0.5) !important;
}

.dark-mode .filter-toggle .v-btn {
  color: #ffffff !important;
}

.dark-mode .filter-toggle .v-btn--active {
  color: #ffffff !important;
  background: var(--dark-accent) !important;
}

.dark-mode .sort-btn {
  color: #ffffff !important;
}

/* Dark Mode - Sidebar Filter */
.dark-mode .sidebar-filter-panel {
  background: rgba(30, 21, 53, 0.88) !important;
  backdrop-filter: blur(6px) !important;
  -webkit-backdrop-filter: blur(6px) !important;
  border-right: 1px solid var(--dark-border) !important;
  box-shadow: 2px 0 20px rgba(0, 0, 0, 0.2) !important;
}

.dark-mode .sidebar-header {
  background: transparent !important;
  border-bottom: 1px solid var(--dark-border) !important;
}

.dark-mode .sidebar-title-section h3,
.dark-mode .sidebar-title-section .inventory-count,
.dark-mode .sidebar-title,
.dark-mode .sidebar-count {
  color: #ffffff !important;
}

.dark-mode .sidebar-divider {
  color: rgba(255, 255, 255, 0.5) !important;
}

.dark-mode .category-tabs {
  background: transparent !important;
  border: none !important;
}

.dark-mode .category-tab {
  background: rgba(30, 21, 53, 0.8) !important;
  border: 1px solid rgba(124, 92, 255, 0.4) !important;
  color: #ffffff !important;
  font-weight: 500 !important;
}

.dark-mode .category-tab:hover {
  background: rgba(124, 92, 255, 0.3) !important;
  border-color: rgba(124, 92, 255, 0.6) !important;
}

.dark-mode .category-tab.active {
  background: var(--dark-accent) !important;
  border-color: var(--dark-accent) !important;
  color: #ffffff !important;
}

.dark-mode .category-tab .tab-count {
  color: #b0a8c9 !important;
}

.dark-mode .category-tab.active .tab-count {
  color: rgba(255, 255, 255, 0.8) !important;
}

.dark-mode .sidebar-filter-section {
  border-bottom: 1px solid var(--dark-border) !important;
  background: transparent !important;
}

.dark-mode .filter-header {
  background: rgba(30, 21, 53, 0.6) !important;
  border-bottom: 1px solid var(--dark-border) !important;
  color: #ffffff !important;
}

.dark-mode .filter-label {
  color: #ffffff !important;
  font-weight: 600 !important;
}

.dark-mode .filter-body {
  background: transparent !important;
}

.dark-mode .filter-group-sidebar {
  background: transparent !important;
}

.dark-mode .filter-group-header {
  color: #ffffff !important;
}

.dark-mode .filter-group-title {
  color: #ffffff !important;
}

.dark-mode .filter-options {
  background: transparent !important;
}

.dark-mode .filter-option {
  background: transparent !important;
  color: #ffffff !important;
}

.dark-mode .filter-option input[type="checkbox"] {
  accent-color: #7c5cff !important;
}

.dark-mode .filter-section-title {
  color: #ffffff !important;
  border-bottom-color: var(--dark-border) !important;
}

.dark-mode .filter-section-content {
  color: #e0e0e0 !important;
}

/* Dark Mode - Checkbox Labels */
.dark-mode .type-checkbox-group label,
.dark-mode .condition-checkbox-group label,
.dark-mode .checkbox-item label,
.dark-mode .v-input--checkbox label,
.dark-mode .v-label {
  color: #ffffff !important;
}

.dark-mode .type-count,
.dark-mode .condition-count,
.dark-mode .item-count {
  color: #b0a8c9 !important;
  background: rgba(124, 92, 255, 0.2) !important;
  padding: 2px 8px !important;
  border-radius: 12px !important;
}

.dark-mode .filter-section {
  border-bottom: 1px solid var(--dark-border) !important;
}

.dark-mode .filter-section-header {
  color: #ffffff !important;
  font-weight: 600 !important;
}

.dark-mode .sidebar-actions {
  border-top: 1px solid var(--dark-border) !important;
  background: rgba(124, 92, 255, 0.05) !important;
}

/* Dark Mode - Select/Dropdown in Sidebar */
.dark-mode .v-select .v-input__control {
  background: rgba(124, 92, 255, 0.1) !important;
  border-radius: 8px !important;
}

.dark-mode .v-select .v-select__selection {
  color: #ffffff !important;
}

.dark-mode .v-select .v-input__append-inner .v-icon {
  color: #ffffff !important;
}

/* Dark Mode - Vehicle Cards - Using ::v-deep for scoped styles */
.dark-mode .v-card,
.dark-mode .truecar-vehicle-card,
.dark-mode::v-deep .v-card,
.dark-mode::v-deep .truecar-vehicle-card {
  background: rgba(30, 21, 53, 0.88) !important;
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  border: 1px solid var(--dark-border) !important;
  border-radius: 12px !important;
}

.dark-mode::v-deep .truecar-vehicle-card .card-body {
  background: transparent !important;
}

.dark-mode .v-card:hover,
.dark-mode .truecar-vehicle-card:hover {
  border-color: var(--dark-accent) !important;
  box-shadow: 0 8px 32px rgba(124, 92, 255, 0.4) !important;
}

.dark-mode .v-card .v-card__title,
.dark-mode .v-card .vehicle-title,
.dark-mode .truecar-vehicle-card .vehicle-title,
.dark-mode::v-deep .vehicle-title {
  color: #ffffff !important;
}

.dark-mode .v-card .v-card__subtitle,
.dark-mode .v-card .vehicle-subtitle {
  color: #ffffff !important;
}

/* Override for price - keep green color */
.dark-mode .v-card .vehicle-price,
.dark-mode .truecar-vehicle-card .price-display,
.dark-mode .price-display,
.dark-mode::v-deep .price-display {
  color: #10b981 !important;
  font-weight: 700 !important;
}

.dark-mode .v-card .vehicle-dealer,
.dark-mode .truecar-vehicle-card .dealer-name,
.dark-mode::v-deep .dealer-name {
  color: #ffffff !important;
}

.dark-mode .truecar-vehicle-card .dealer-location,
.dark-mode .truecar-vehicle-card .dealer-address,
.dark-mode::v-deep .dealer-location,
.dark-mode::v-deep .dealer-address {
  color: #ffffff !important;
}

/* Dark Mode - Vehicle Card Specs */
.dark-mode .truecar-vehicle-card .spec-item,
.dark-mode::v-deep .spec-item {
  background: rgba(124, 92, 255, 0.3) !important;
  color: #ffffff !important;
  border: 1px solid rgba(124, 92, 255, 0.5) !important;
}

.dark-mode .truecar-vehicle-card .network-text,
.dark-mode::v-deep .network-text {
  color: #ffffff !important;
}

.dark-mode .truecar-vehicle-card .price-section,
.dark-mode::v-deep .price-section {
  border-top: 1px solid var(--dark-border) !important;
}

.dark-mode .truecar-vehicle-card .dealer-info,
.dark-mode::v-deep .dealer-info {
  border-top: 1px solid var(--dark-border) !important;
}

.dark-mode .truecar-vehicle-card .image-container,
.dark-mode::v-deep .image-container {
  background: linear-gradient(135deg, #251a45 0%, #1a1230 100%) !important;
}

/* Dark Mode - Buttons */
.dark-mode .v-btn.primary,
.dark-mode .search-btn,
.dark-mode .filter-search-btn,
.dark-mode .truecar-vehicle-card .view-btn,
.dark-mode::v-deep .view-btn {
  background: var(--dark-accent) !important;
  color: #ffffff !important;
  font-weight: 600 !important;
}

.dark-mode .v-btn.outlined,
.dark-mode .clear-filters-btn,
.dark-mode .truecar-vehicle-card .interested-btn,
.dark-mode::v-deep .interested-btn {
  background: transparent !important;
  border: 1px solid var(--dark-border) !important;
  color: #ffffff !important;
}

.dark-mode .truecar-vehicle-card .interested-btn:hover,
.dark-mode::v-deep .interested-btn:hover {
  background: rgba(124, 92, 255, 0.2) !important;
  border-color: var(--dark-accent) !important;
}

/* Dark Mode - Inputs */
.dark-mode .v-text-field input,
.dark-mode .v-select input {
  color: #ffffff !important;
}

.dark-mode .v-text-field .v-input__slot,
.dark-mode .v-select .v-input__slot {
  background: rgba(124, 92, 255, 0.1) !important;
  border: 1px solid var(--dark-border) !important;
}

/* Dark Mode - Slider */
.dark-mode .v-slider__track-background {
  background: #251a45 !important;
}

.dark-mode .v-slider__track-fill {
  background: var(--dark-accent) !important;
}

.dark-mode .v-slider__thumb {
  background: var(--dark-accent) !important;
  border: 2px solid #ffffff !important;
}

/* Dark Mode - Checkbox */
.dark-mode .v-input--checkbox .v-icon {
  color: #ffffff !important;
}

.dark-mode .v-input--checkbox.v-input--is-label-active .v-icon,
.dark-mode .v-input--selection-controls__input .v-icon {
  color: var(--dark-accent) !important;
}

/* Dark Mode - Menu/Dropdown */
.dark-mode .v-menu__content {
  background: #1e1535 !important;
  border: 1px solid var(--dark-border) !important;
}

.dark-mode .v-list {
  background: #1e1535 !important;
}

.dark-mode .v-list-item {
  color: #ffffff !important;
}

.dark-mode .v-list-item:hover {
  background: rgba(124, 92, 255, 0.2) !important;
}

/* Dark Mode - Scrollbar */
.dark-mode ::-webkit-scrollbar {
  width: 8px;
}

.dark-mode ::-webkit-scrollbar-track {
  background: #0f0a1f;
}

.dark-mode ::-webkit-scrollbar-thumb {
  background: #251a45;
  border-radius: 4px;
}

.dark-mode ::-webkit-scrollbar-thumb:hover {
  background: var(--dark-accent);
}

/* Dark Mode - Additional Elements */
.dark-mode .sidebar-title-section {
  color: #ffffff !important;
}

.dark-mode .type-checkbox-item,
.dark-mode .condition-checkbox-item {
  color: #ffffff !important;
}

.dark-mode .range-values,
.dark-mode .price-range-display,
.dark-mode .mileage-range-display {
  color: #ffffff !important;
  font-weight: 500 !important;
}

/* Dark Mode - Filter Groups */
.dark-mode .filter-group-sidebar {
  border-bottom: 1px solid var(--dark-border) !important;
}

.dark-mode .filter-group-header {
  color: #ffffff !important;
}

.dark-mode .filter-group-title {
  color: #ffffff !important;
  font-weight: 600 !important;
}

.dark-mode .filter-range-label {
  color: #b0a8c9 !important;
}

/* Dark Mode - Slider Labels */
.dark-mode .slider-labels,
.dark-mode .slider-min,
.dark-mode .slider-max {
  color: #ffffff !important;
}

.dark-mode .filter-slider-container {
  background: rgba(124, 92, 255, 0.05) !important;
  padding: 12px !important;
  border-radius: 8px !important;
}

/* Dark Mode - V-Range-Slider */
.dark-mode .v-slider__track-container {
  background: #251a45 !important;
}

.dark-mode .v-slider__track-fill {
  background: var(--dark-accent) !important;
}

.dark-mode .v-slider__thumb {
  background: var(--dark-accent) !important;
}

/* Dark Mode - Sidebar Filter Actions */
.dark-mode .sidebar-filter-actions {
  border-top: 1px solid var(--dark-border) !important;
  padding-top: 16px !important;
  margin-top: 16px !important;
}

.dark-mode .sidebar-filter-actions .search-btn {
  background: var(--dark-accent) !important;
  color: #ffffff !important;
  font-weight: 600 !important;
}

.dark-mode .sidebar-filter-actions .clear-btn {
  background: transparent !important;
  border: 1px solid var(--dark-border) !important;
  color: #ffffff !important;
}

.dark-mode .sidebar-filter-actions .clear-btn:hover {
  background: rgba(124, 92, 255, 0.2) !important;
}

/* Dark Mode - Filter Toggle Buttons (All, Available, Pending, Sold) */
.dark-mode .filter-toggle {
  background: rgba(124, 92, 255, 0.1) !important;
  border-radius: 8px !important;
}

.dark-mode .filter-toggle .v-btn {
  color: #ffffff !important;
  background: transparent !important;
}

.dark-mode .filter-toggle .v-btn--active {
  background: var(--dark-accent) !important;
  color: #ffffff !important;
}

.dark-mode .filter-toggle .v-btn:hover {
  background: rgba(124, 92, 255, 0.3) !important;
}

/* Dark Mode - Filter Tabs */
.dark-mode .filter-tabs {
  background: transparent !important;
}

/* Dark Mode - Sort Dropdown */
.dark-mode .sort-select .v-input__control {
  background: rgba(124, 92, 255, 0.1) !important;
}

.dark-mode .sort-select .v-select__selection {
  color: #ffffff !important;
}

/* Dark Mode - Pagination */
.dark-mode .v-pagination .v-pagination__item {
  background: rgba(124, 92, 255, 0.1) !important;
  color: #ffffff !important;
}

.dark-mode .v-pagination .v-pagination__item--active {
  background: var(--dark-accent) !important;
}

/* Dark Mode - Card Body */
.dark-mode .card-body {
  background: transparent !important;
}

/* Dark Mode - Main Content Area */
.dark-mode .main-content-area {
  background: transparent !important;
}

/* Dark Mode - Inventory Layout */
.dark-mode .inventory-layout {
  background: transparent !important;
}

/* Dark Mode - Icons */
.dark-mode .v-icon {
  color: #ffffff !important;
}

.dark-mode .filter-toggle-icon {
  color: #ffffff !important;
}

.dark-mode .tab-icon {
  color: currentColor !important;
}

/* Dark Mode - Spec Item Icons */
.dark-mode .spec-item .v-icon {
  color: #b0a8c9 !important;
}

/* Dark Mode - Sort Button */
.dark-mode .inventory-sort .v-btn {
  color: #ffffff !important;
}

.dark-mode .inventory-sort .v-icon {
  color: #ffffff !important;
}

/* Dark Mode - Text Elements */
.dark-mode span,
.dark-mode p {
  color: inherit;
}

/* Dark Mode - Hover Effects */
.dark-mode .v-card:hover .vehicle-title {
  color: var(--dark-accent) !important;
}

/* Dark Mode - Empty State */
.dark-mode .empty-title {
  color: #ffffff !important;
}

.dark-mode .empty-subtitle {
  color: #b0a8c9 !important;
}

/* Dark Mode - Loading Text */
.dark-mode .loading-text {
  color: #ffffff !important;
}

/* Dark Mode - Sidebar Checkbox Group */
.dark-mode .type-checkbox-group,
.dark-mode .condition-checkbox-group {
  color: #ffffff !important;
}

.dark-mode .type-checkbox-group .v-input--checkbox,
.dark-mode .condition-checkbox-group .v-input--checkbox {
  color: #ffffff !important;
}

/* Dark Mode - Make/Model Select */
.dark-mode .make-select,
.dark-mode .model-select {
  background: rgba(124, 92, 255, 0.1) !important;
  border-radius: 8px !important;
}

.dark-mode .make-select .v-input__control,
.dark-mode .model-select .v-input__control {
  color: #ffffff !important;
}

/* Dark Mode - Price Display Colors */
.dark-mode .price-display {
  color: #10b981 !important;
}

/* Dark Mode - Filter Options (Type, Price, Mileage) */
.dark-mode .filter-options {
  color: #ffffff !important;
}

.dark-mode .filter-option {
  color: #ffffff !important;
}

.dark-mode .filter-option .option-label {
  color: #ffffff !important;
}

.dark-mode .filter-option .option-count {
  color: #b0a8c9 !important;
}

.dark-mode .filter-group-count {
  color: #b0a8c9 !important;
}

/* Dark Mode - Ensure all sidebar text is white */
.dark-mode .sidebar-filter-panel,
.dark-mode .sidebar-filter-panel * {
  color: #ffffff;
}

.dark-mode .sidebar-filter-panel .v-icon {
  color: #ffffff !important;
}

/* =============================================
   Mobile Bottom Navigation
   ============================================= */
.mobile-bottom-nav {
  display: none;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: 70px;
  z-index: 1000;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(255, 255, 255, 0.95) 100%);
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(20px);
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.mobile-bottom-nav.dark-mode {
  background: linear-gradient(180deg, rgba(30, 30, 46, 0.98) 0%, rgba(30, 30, 46, 0.95) 100%);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.3);
}

.bottom-nav-container {
  display: flex;
  justify-content: space-around;
  align-items: center;
  height: 100%;
  max-width: 500px;
  margin: 0 auto;
  padding: 0 10px;
}

.bottom-nav-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  padding: 8px 16px;
  border-radius: 16px;
  min-width: 70px;
  position: relative;
}

.bottom-nav-item:hover {
  background: rgba(59, 130, 246, 0.1);
  transform: translateY(-2px);
}

.bottom-nav-item:active {
  transform: translateY(0);
}

.bottom-nav-item.active {
  background: rgba(59, 130, 246, 0.15);
}

.bottom-nav-item.active .bottom-nav-icon {
  color: #3b82f6;
}

.bottom-nav-item.active .bottom-nav-label {
  color: #3b82f6;
  font-weight: 600;
}

.bottom-nav-icon {
  font-size: 24px;
  color: #64748b;
  margin-bottom: 4px;
  transition: all 0.3s ease;
}

.bottom-nav-label {
  font-size: 11px;
  font-weight: 500;
  color: #64748b;
  letter-spacing: 0.3px;
}

/* Dark mode bottom nav */
.mobile-bottom-nav.dark-mode .bottom-nav-icon {
  color: #a0aec0;
}

.mobile-bottom-nav.dark-mode .bottom-nav-label {
  color: #a0aec0;
}

.mobile-bottom-nav.dark-mode .bottom-nav-item:hover {
  background: rgba(124, 92, 255, 0.15);
}

.mobile-bottom-nav.dark-mode .bottom-nav-item.active {
  background: rgba(124, 92, 255, 0.2);
}

.mobile-bottom-nav.dark-mode .bottom-nav-item.active .bottom-nav-icon {
  color: #a78bfa;
}

.mobile-bottom-nav.dark-mode .bottom-nav-item.active .bottom-nav-label {
  color: #a78bfa;
}

/* Show bottom nav only on mobile */
@media (max-width: 768px) {
  .mobile-bottom-nav {
    display: block;
  }

  /* Add padding to main content to account for bottom nav */
  .main-content-area {
    padding-bottom: 80px;
  }

  .footer-section {
    padding-bottom: 80px;
  }
}

/* Safe area for iOS devices */
@supports (padding-bottom: env(safe-area-inset-bottom)) {
  .mobile-bottom-nav {
    padding-bottom: env(safe-area-inset-bottom);
    height: calc(70px + env(safe-area-inset-bottom));
  }
}
</style>

<script>
var slides = document.querySelectorAll(".slide");
var currentSlide = 0;


import AppBarBlurAuth from "@/components/AppBarBlurAuth";
import VCard from 'vcard-creator'
import { FadeTransition } from "vue2-transitions";
import Query from "@/components/Forms/Query.vue";
import ProfileCards from "@/components/Forms/VehicleCardList.vue";
import InventoryFilterModal from "@/components/InventoryFilterModal.vue";
import SyncGuideXChat from "@/components/SyncGuideXChat.vue";
import VehicleComparisonCard from "@/components/VehicleComparisonCard.vue";
import DigitalAssistantChat from "@/components/DigitalAssistantChat.vue";
import TrueCarVehicleCard from "@/components/TrueCarVehicleCard.vue";

export default {
  name: "page-layout",
  components: {
    AppBarBlurAuth,
    FadeTransition,
    ProfileCards,
    VCard,
    Query,
    InventoryFilterModal,
    SyncGuideXChat,
    VehicleComparisonCard,
    DigitalAssistantChat,
    TrueCarVehicleCard
  },
  props: {
    isModalMode: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
       checkbox: false,
       showpassword : false,
       email : '',
      password : '',
      sales_agent_count: 0,
      search_rep: '',
      searchTerm: '', // Add searchTerm for filtering
      ref_agent_count: 0,
      phone : '',
      loader : true,
      showLoadingModal: false, // Enhanced loading modal - only show for AI searches
      currentLoadingText: 'One moment, I’m syncing over a dozen marketplaces to find you the best listings. Let’s see what Facebook, Craigslist, and the dealer network has for you today…',
      loadingTextIndex: 0,
      loadingDotIndex: 1,
      loadingTextInterval: null,
      loadingDotInterval: null,
      searching_external: false, // Flag to indicate external search is in progress
      isAiSearch: false, // Flag to track if this is an AI search
      background: '',
      vimeo_account: '',
      vimeo_id : '',
      reps_flag : false,
      company_settings : '',
      reps_users : [], // Initialize as empty array for better reactivity
      external_search_done: false, // Flag to indicate external search is complete
      overlay : false,
      index: 0,
      count_dealer: 0,
      count_vehicle: 0,
      count_make: 0,
      showPassword : false,
      zoom_value : '',
      base64Image :'',
      avatar_mimeType: '',
      event_value :'',
      dialog: false,
      color : '',
      videos : [],
      pageFlag: 'init',
      backgroundLogo: localStorage.getItem('admin_bg'),
      isDark :true,
      isDarkMode: localStorage.getItem('herrera_theme') !== 'light',
      mobileMenuOpen: false,
      buttonFlag   : false,
      loadingLogo : false,
      url : '',
      items: [
        ],
      company : '',
      vcard : '',
      loadingFlag : false,
      base_url_img : process.env.VUE_APP_WASBI_URL,
      pageTitle: '',
      // Added for inventory filtering
      showFilterModal: false,
      showSyncGuideX: false, // Control SyncGuideX chat modal
      showFilters: false, // Control expandable filter panel
      make_option: [],
      model_option: [],
      year_option: [],
      makeOptions: [], // For the new filter system

      // New filter system (matching Inventory.vue)
      filters: {
        minPrice: null,
        maxPrice: null,
        make: null,
        minYear: null,
        maxYear: null,
        minMileage: null,
        maxMileage: null,
        bodyStyle: null,
        drivetrain: null,
        fuelType: null,
        transmission: null
      },

      // Filter options from VehicleDetail.vue
      bodyStyleOptions: ['Coupe','Convertible','Sedan','Hybrid/Electric','Crossover/SUV','Truck','4WD/AWD','Van','Wagon','Hatchback'],
      drivetrainOptions: ['4x4', 'AWD', 'FWD', 'RWD'],
      fuelTypeOptions: ["Gasoline", "Diesel", "Electric", "Hybrid", "Plug-in Hybrid", "CNG", "LPG", "Hydrogen", "Ethanol", "Biodiesel", "Synthetic Fuels", "Solar"],
      transmissionOptions: ['Automatic', 'Manual', 'CVT'],

      // Sorting options
      currentSort: { value: 'price_asc', label: 'Price: Low to High' },
      sortOptions: [
        { value: 'price_asc', label: 'Price: Low to High' },
        { value: 'price_desc', label: 'Price: High to Low' },
        { value: 'year_desc', label: 'Year: Newest First' },
        { value: 'year_asc', label: 'Year: Oldest First' },
        { value: 'mileage_asc', label: 'Mileage: Low to High' },
        { value: 'mileage_desc', label: 'Mileage: High to Low' }
      ],

      // Sidebar filter panel state
      sidebarFilterExpanded: true,
      priceFilterExpanded: true,
      mileageFilterExpanded: true,
      sidebarFilters: {
        selectedTypes: [],
        priceRange: [5000, 50000],
        mileageRange: [0, 100000]
      },
      vehicleTypeOptions: [
        { value: 'Sedan',         label: 'Sedan',            count: 0 },
        { value: 'Crossover/SUV', label: 'SUV / Crossover',  count: 0 },
        { value: 'Truck',         label: 'Truck',            count: 0 },
        { value: 'Coupe',         label: 'Coupe',            count: 0 },
        { value: 'Convertible',   label: 'Convertible',      count: 0 },
        { value: 'Hatchback',     label: 'Hatchback',        count: 0 },
        { value: 'Van',           label: 'Van',              count: 0 },
        { value: 'Wagon',         label: 'Wagon',            count: 0 },
        { value: 'Hybrid/Electric', label: 'Hybrid / EV',   count: 0 },
        { value: '4WD/AWD',       label: '4WD / AWD',        count: 0 },
      ],
      // Category tabs for header
      activeCategory: 'all',
      categoryTabs: [
        { value: 'all', label: 'All', icon: 'mdi-car-multiple' },
        { value: 'available', label: 'Available', icon: 'mdi-check-circle-outline' },
        { value: 'pending', label: 'Pending', icon: 'mdi-clock-outline' },
        { value: 'sold', label: 'Sold', icon: 'mdi-tag-check' },
        { value: 'gettable', label: 'Gettable', icon: 'mdi-car-search' }
      ],

      // Filter timeout for debouncing
      filterTimeout: null,

      // Lazy loading state (for no-domain / all-vehicles mode)
      lazyPageSize: 10,
      lazyOffset: 0,
      hasMoreLazy: false,
      isLoadingMore: false,
      lazyObserver: null,

      // Sidebar counts from API (accurate totals regardless of lazy-loaded batch)
      apiBodyStyleCounts: {},
      apiTotalAvailable: 0,
      loadingMessages: [
        'We are searching the best results for you...',
        'Finding perfect vehicles in your area...',
        'Analyzing thousands of vehicle listings...',
        'Matching your preferences with available inventory...',
        'Discovering great deals just for you...',
        'Almost ready with your personalized results...'
      ],
      price_range: {
        min: 0,
        max: 100000
      },
      filter: {
        type: 'All',
        make: '',
        model: '',
        year_min: '',
        year_max: '',
        price_min: '',
        price_max: '',
        odometer_min: '',
        odometer_max: '',
        zip_code: '',
        radius: 50,
      }
    };
  },

  computed: {
    // Get current domain for digital assistant
    currentDomain() {
      return this.getSubdomain();
    },

    // Array of loading messages to cycle through
    loadingMessages() {
      return [
        'We are searching the best results for you...',
        'Finding perfect vehicles in your area...',
        'Analyzing thousands of vehicle listings...',
        'Matching your preferences with available inventory...',
        'Discovering great deals just for you...',
        'Almost ready with your personalized results...'
      ];
    },

    // Check if we have any vehicles loaded
    hasVehicles() {
      return this.reps_users && this.reps_users.length > 0;
    },

    // Check if we're in comparison mode
    isComparisonMode() {
      return this.$route.query.comparison_mode === 'true' || this.$route.query.comparison_mode === true;
    },

    // Count external vehicles for display
    externalVehicleCount() {
      if (!this.reps_users) return 0;
      return this.reps_users.filter(vehicle => vehicle.is_external).length;
    },

    // Check if we have external results
    hasExternalResults() {
      return this.externalVehicleCount > 0;
    },

    // Filtered users for search functionality with estimated payments
    filteredUsers() {
      console.log('🔍 filteredUsers computed - reps_users:', this.reps_users);
      console.log('🔍 filteredUsers computed - reps_users length:', this.reps_users ? this.reps_users.length : 'null/undefined');

      if (!this.reps_users) {
        console.log('🔍 filteredUsers: reps_users is null/undefined, returning empty array');
        return [];
      }

      let vehicles = [...this.reps_users];

      // Apply category filter (status: all, available, pending, sold, gettable)
      if (this.activeCategory && this.activeCategory !== 'all') {
        console.log('🔍 filteredUsers: applying category filter:', this.activeCategory);
        vehicles = vehicles.filter(vehicle => {
          // Check explicit status field first (domain-based inventory)
          const status = (vehicle.status || vehicle.vehicle_status || '').toLowerCase();
          if (status) return status === this.activeCategory;

          // DB vehicles (from getAllVehicles) use is_sold_out instead of a status field.
          // The API already filters is_sold_out=false, so all loaded vehicles are available.
          if (this.activeCategory === 'available') return true;

          // Pending / sold / gettable don't exist in this data source → show nothing
          return false;
        });
        console.log('🔍 filteredUsers: after category filter:', vehicles.length);
      }

      // Apply type filter (SUV, Sedan, Truck, etc.)
      if (this.sidebarFilters.selectedTypes && this.sidebarFilters.selectedTypes.length > 0) {
        console.log('🔍 filteredUsers: applying type filter:', this.sidebarFilters.selectedTypes);
        vehicles = vehicles.filter(vehicle => {
          const bodyType = (vehicle.body_type || vehicle.bodyStyle || vehicle.body_style || '').toLowerCase();
          return this.sidebarFilters.selectedTypes.some(type =>
            bodyType.toLowerCase().includes(type.toLowerCase())
          );
        });
        console.log('🔍 filteredUsers: after type filter:', vehicles.length);
      }

      // Apply price filter
      if (this.sidebarFilters.priceRange) {
        const [minPrice, maxPrice] = this.sidebarFilters.priceRange;
        if (minPrice > 5000 || maxPrice < 50000) { // Only filter if not default range
          console.log('🔍 filteredUsers: applying price filter:', minPrice, '-', maxPrice);
          vehicles = vehicles.filter(vehicle => {
            const price = parseFloat(vehicle.price || vehicle.amount || 0);
            return price >= minPrice && price <= maxPrice;
          });
          console.log('🔍 filteredUsers: after price filter:', vehicles.length);
        }
      }

      // Apply mileage filter
      if (this.sidebarFilters.mileageRange) {
        const [minMileage, maxMileage] = this.sidebarFilters.mileageRange;
        if (minMileage > 0 || maxMileage < 100000) { // Only filter if not default range
          console.log('🔍 filteredUsers: applying mileage filter:', minMileage, '-', maxMileage);
          vehicles = vehicles.filter(vehicle => {
            const mileage = parseInt(vehicle.odometer || vehicle.mileage || 0);
            return mileage >= minMileage && mileage <= maxMileage;
          });
          console.log('🔍 filteredUsers: after mileage filter:', vehicles.length);
        }
      }

      // Apply search filter if search term exists
      if (this.searchTerm && this.searchTerm.trim() !== '') {
        console.log('🔍 filteredUsers: applying search filter for term:', this.searchTerm);
        const searchLower = this.searchTerm.toLowerCase().trim();
        vehicles = vehicles.filter(vehicle => {
          const make = (vehicle.make || '').toLowerCase();
          const model = (vehicle.model || '').toLowerCase();
          const year = (vehicle.year || '').toString();
          const vin = (vehicle.vin || '').toLowerCase();
          const stockNumber = (vehicle.stock_number || vehicle.stockNumber || '').toLowerCase();
          const trim = (vehicle.trim || '').toLowerCase();
          const color = (vehicle.color || vehicle.exterior_color || '').toLowerCase();
          const bodyType = (vehicle.body_type || vehicle.bodyStyle || '').toLowerCase();

          return make.includes(searchLower) ||
                 model.includes(searchLower) ||
                 year.includes(searchLower) ||
                 vin.includes(searchLower) ||
                 stockNumber.includes(searchLower) ||
                 trim.includes(searchLower) ||
                 color.includes(searchLower) ||
                 bodyType.includes(searchLower);
        });
        console.log('🔍 filteredUsers: filtered results:', vehicles.length);
      }

      // Note: Estimated payment is now calculated by the API server-side
      // Debug: Log payment data for first vehicle
      if (vehicles.length > 0) {
        console.log('🔍 API payment data for first vehicle:');
        console.log('  Vehicle price/amount:', vehicles[0].price || vehicles[0].amount);
        console.log('  API estimated_payment:', vehicles[0].estimated_payment);
        console.log('  Loan APR rate:', vehicles[0].loan_apr_rate);
        console.log('  Loan term months:', vehicles[0].loan_term_months);
      }

      // Note: Sorting and estimated payments are now handled by the API (server-side) like Inventory.vue
      console.log('🔍 filteredUsers: returning vehicles with API sorting and payments:', vehicles.length);
      return vehicles;
    },

    // Check if there are active filters (updated for new filter system)
    hasActiveFilters() {
      if (!this.filters) return false;
      return Object.values(this.filters).some(value => {
        return value !== null && value !== '' && value !== undefined;
      });
    },

    // Count active filters (updated for new filter system)
    activeFiltersCount() {
      if (!this.filters) return 0;
      let count = 0;

      // Count individual filters
      if (this.filters.minPrice || this.filters.maxPrice) count++;
      if (this.filters.make) count++;
      if (this.filters.minYear || this.filters.maxYear) count++;
      if (this.filters.minMileage || this.filters.maxMileage) count++;
      if (this.filters.bodyStyle) count++;
      if (this.filters.drivetrain) count++;
      if (this.filters.fuelType) count++;
      if (this.filters.transmission) count++;

      return count;
    },

    // Check if sidebar filters are active
    hasSidebarFiltersActive() {
      if (!this.sidebarFilters) return false;

      // Check if any type is selected
      if (this.sidebarFilters.selectedTypes && this.sidebarFilters.selectedTypes.length > 0) return true;

      // Check if price range is different from default
      if (this.sidebarFilters.priceRange &&
          (this.sidebarFilters.priceRange[0] > 5000 || this.sidebarFilters.priceRange[1] < 50000)) return true;

      // Check if mileage range is different from default
      if (this.sidebarFilters.mileageRange &&
          (this.sidebarFilters.mileageRange[0] > 0 || this.sidebarFilters.mileageRange[1] < 100000)) return true;

      // Check if category is not "all"
      if (this.activeCategory && this.activeCategory !== 'all') return true;

      return false;
    },

    // Count of sidebar filters applied
    sidebarFiltersCount() {
      let count = 0;

      if (this.sidebarFilters.selectedTypes && this.sidebarFilters.selectedTypes.length > 0) count++;
      if (this.sidebarFilters.priceRange &&
          (this.sidebarFilters.priceRange[0] > 5000 || this.sidebarFilters.priceRange[1] < 50000)) count++;
      if (this.sidebarFilters.mileageRange &&
          (this.sidebarFilters.mileageRange[0] > 0 || this.sidebarFilters.mileageRange[1] < 100000)) count++;
      if (this.activeCategory && this.activeCategory !== 'all') count++;

      return count;
    }
  },

  // Add watchers to handle append mode
  watch: {
     pageTitle() {
      this.$meta().refresh();
    },

    // Watch for changes in reps_users to handle loading modal and load make options
    reps_users: {
      handler(newVehicles) {
        // Hide loading modal when vehicles are loaded (whether 0 or more results)
        // This is a backup in case the main API response handler doesn't hide it
        if (newVehicles !== null && newVehicles !== undefined) {
          console.log('🔍 Watcher: reps_users changed, hiding loading modal. Vehicle count:', newVehicles.length);
          if(newVehicles.length > 0){
            this.hideLoadingModal();
          }

          // Load make options from the new vehicles
          this.loadMakeOptions();

          // Update vehicle type counts for sidebar filter
          this.updateVehicleTypeCounts();

          // Update category counts (status: available, pending, sold, etc.)
          this.updateCategoryCounts();
        }
      },
      deep: true,
      immediate: false
    }
  },

  beforeCreate: function () {

},
created: function () {
  // Just basic setup in created
},
mounted : function () {
  console.log('Component mounted, showLoadingModal:', this.showLoadingModal);

  // Get URL parameters to check if this is an AI search
  const urlParams = this.$route.query;
  const isAiSearch = urlParams.ai_search === '1';

  // Set the AI search flag for use in template
  this.isAiSearch = isAiSearch;

  console.log('Is AI search:', isAiSearch);

  // Only show enhanced loading modal for AI searches
  if (isAiSearch) {
    console.log('AI search detected, showing enhanced loading modal');
    this.showLoadingModal = true;
    this.startLoadingAnimations();
  } else {
    console.log('Normal search detected, hiding enhanced loading modal but keeping regular loader');
    this.showLoadingModal = false;
    // For normal searches, keep the regular loader active
    this.loader = true;
  }

  // Get the current domain
  const domain = this.getSubdomain();

  if(domain){
    // Get company info and settings
    this.getCompanyInfo(domain);
    this.getSettings();

    // Get inventory filter options
    this.getInventoryFilter(domain);

    // If we have URL parameters, use them for the initial search
    if (Object.keys(urlParams).length > 0) {
      console.log("Found URL parameters, using for initial search:", urlParams);

      this.getInventory(domain, urlParams);
    } else {
      // Otherwise, check if we have saved filters in localStorage
      const savedFilters = localStorage.getItem('vehicle_filter');
      if (savedFilters) {
        try {
          const filters = JSON.parse(savedFilters);
          // Check if filters are recent (within the last 24 hours)
          const now = new Date().getTime();
          const filterTime = filters.timestamp || 0;
          const isRecent = (now - filterTime) < (24 * 60 * 60 * 1000); // 24 hours

          if (isRecent) {
            console.log("Found recent saved filters, using for initial search:", filters);
            // Create a query object from the saved filters
            const query = {
              makes: filters.makes && filters.makes.length > 0 ? filters.makes.join(',') : '',
              models: filters.models && filters.models.length > 0 ? filters.models.join(',') : '',
              states: filters.states && filters.states.length > 0 ? filters.states.join(',') : '',
              year_min: filters.year_min || '',
              year_max: filters.year_max || '',
              price_min: filters.price_min || '',
              price_max: filters.price_max || '',
              odometer_min: filters.odometer_min || '',
              odometer_max: filters.odometer_max || '',
              zip_code: filters.zip_code || '',
              radius: filters.radius || 50,
              credit_score: 641,
              bhph: 0
            };

            // Update URL with saved filters
            this.updateUrlWithFilters(query);

            // Get inventory with saved filters
            this.getInventory(domain, query);
          } else {
            // Filters are old, get inventory without filters
            console.log("Saved filters are too old, getting inventory without filters");
            this.getInventory(domain);
          }
        } catch (error) {
          console.error('Error loading saved filters:', error);
          // If there's an error, get inventory without filters
          this.getInventory(domain);
        }
      } else {
        // No saved filters, get inventory without filters
        console.log("No saved filters found, getting inventory without filters");
        this.getInventory(domain);
      }
    }
  } else {
    // No subdomain found — load all vehicles from admin panel
    this.loadAllVehicles();
  }

  // Register digital assistant instance globally
  this.$nextTick(() => {
    if (this.$refs.digitalAssistant) {
      this.$digitalAssistant.setInstance(this.$refs.digitalAssistant);
    }
  });

  // Listen for global vehicle tracking events
  this.$root.$on('vehicle-viewed', this.handleGlobalVehicleView);
  this.$root.$on('vehicle-detail-viewed', this.handleGlobalVehicleDetailView);
  this.$root.$on('vehicle-search', this.handleGlobalVehicleSearch);
},
beforeDestroy() {
  // Clean up intervals when component is destroyed
  this.stopLoadingAnimations();

  // Clean up lazy scroll observer
  this.destroyLazyObserver();

  // Clean up event listeners
  this.$root.$off('vehicle-viewed', this.handleGlobalVehicleView);
  this.$root.$off('vehicle-detail-viewed', this.handleGlobalVehicleDetailView);
  this.$root.$off('vehicle-search', this.handleGlobalVehicleSearch);
},
methods: {
  // Toggle Dark/Light Mode
  toggleDarkMode() {
    this.isDarkMode = !this.isDarkMode;
    localStorage.setItem('herrera_theme', this.isDarkMode ? 'dark' : 'light');
    console.log('🌓 Theme toggled:', this.isDarkMode ? 'Dark Mode' : 'Light Mode');
    // Close mobile menu after action
    this.mobileMenuOpen = false;
  },

  // Toggle Mobile Menu
  toggleMobileMenu() {
    this.mobileMenuOpen = !this.mobileMenuOpen;
    console.log('📱 Mobile menu toggled:', this.mobileMenuOpen);
  },

  // Toggle Sidebar Filter (for mobile)
  toggleSidebarFilter() {
    this.sidebarFilterExpanded = !this.sidebarFilterExpanded;
    this.mobileMenuOpen = false; // Close the mobile menu
    console.log('🔍 Sidebar filter toggled:', this.sidebarFilterExpanded);
  },

  // Handle search input with debouncing
  onSearchInput() {
    // Simple search functionality - the filteredUsers computed property handles the actual filtering
    console.log('Search input changed:', this.searchTerm);
  },

  // Toggle filter panel with debugging
  toggleFilters() {
    console.log('🔧 Toggle filters clicked. Current showFilters:', this.showFilters);
    this.showFilters = !this.showFilters;
    console.log('🔧 New showFilters value:', this.showFilters);

    // Force Vue to update the DOM
    this.$nextTick(() => {
      console.log('🔧 DOM updated. Filter panel should be:', this.showFilters ? 'visible' : 'hidden');
    });
  },

  // Apply filters with debouncing and auto-collapse
  applyFilters() {
    // Auto-collapse the filter panel
    this.showFilters = false;

    clearTimeout(this.filterTimeout);
    this.filterTimeout = setTimeout(() => {
      this.performFilteredSearch();
    }, 300);
  },

  // Perform filtered search
  async performFilteredSearch() {
    const domain = this.getSubdomain();
    if (!domain) return;

    // Build query parameters from filters
    const query = {};

    if (this.filters.minPrice) query.min_price = this.filters.minPrice;
    if (this.filters.maxPrice) query.max_price = this.filters.maxPrice;
    if (this.filters.make) query.make = this.filters.make;
    if (this.filters.minYear) query.min_year = this.filters.minYear;
    if (this.filters.maxYear) query.max_year = this.filters.maxYear;
    if (this.filters.minMileage) query.min_mileage = this.filters.minMileage;
    if (this.filters.maxMileage) query.max_mileage = this.filters.maxMileage;
    if (this.filters.bodyStyle) query.body_style = this.filters.bodyStyle;
    if (this.filters.drivetrain) query.drivetrain = this.filters.drivetrain;
    if (this.filters.fuelType) query.fuel_type = this.filters.fuelType;
    if (this.filters.transmission) query.transmission = this.filters.transmission;

    // Add sort parameter
    query.sort = this.currentSort.value;

    console.log('Applying filters with query:', query);

    // Call the existing inventory method with filters
    this.getInventory(domain, query);
  },

  // Clear all filters and auto-collapse
  clearFilters() {
    // Auto-collapse the filter panel
    this.showFilters = false;

    this.filters = {
      minPrice: null,
      maxPrice: null,
      make: null,
      minYear: null,
      maxYear: null,
      minMileage: null,
      maxMileage: null,
      bodyStyle: null,
      drivetrain: null,
      fuelType: null,
      transmission: null
    };
    this.performFilteredSearch();
  },

  // Clear type filter (select All)
  clearTypeFilter() {
    this.sidebarFilters.selectedTypes = [];
  },

  // Apply sidebar filters - makes API call with filter parameters
  applySidebarFilters() {
    console.log('🔍 Applying sidebar filters:', this.sidebarFilters);
    console.log('🔍 Selected types:', this.sidebarFilters.selectedTypes);
    console.log('🔍 Price range:', this.sidebarFilters.priceRange);
    console.log('🔍 Mileage range:', this.sidebarFilters.mileageRange);

    // Map sidebar filters to main filters
    this.filters.minPrice = this.sidebarFilters.priceRange[0];
    this.filters.maxPrice = this.sidebarFilters.priceRange[1];
    this.filters.minMileage = this.sidebarFilters.mileageRange[0];
    this.filters.maxMileage = this.sidebarFilters.mileageRange[1];

    // Handle body style from selected types
    if (this.sidebarFilters.selectedTypes.length > 0) {
      this.filters.bodyStyle = this.sidebarFilters.selectedTypes.join(',');
    } else {
      this.filters.bodyStyle = null;
    }

    // Apply the filters via API call
    this.performFilteredSearch();
  },

  // Clear sidebar filters
  clearSidebarFilters() {
    console.log('🔍 Clearing sidebar filters');
    this.sidebarFilters = {
      selectedTypes: [],
      priceRange: [5000, 50000],
      mileageRange: [0, 100000]
    };
    this.activeCategory = 'all';

    // Clear main filters and make API call
    this.clearFilters();
  },

  // Update sidebar category tabs and type filter from accurate API totals
  updateSidebarCountsFromApi(apiData) {
    const bodyStyleCounts = apiData.body_style_counts || {};
    const totalAvailable = apiData.total_available || apiData.total || 0;

    // Store for potential reuse
    this.apiBodyStyleCounts = bodyStyleCounts;
    this.apiTotalAvailable = totalAvailable;

    // Map body_style keys (lowercase from backend) to vehicleTypeOptions
    // Matches exact values from the admin panel dropdown:
    // ['Coupe','Convertible','Sedan','Hybrid/Electric','Crossover/SUV','Truck','4WD/AWD','Van','Wagon','Hatchback']
    const bodyStyleMap = {
      'Sedan':           ['sedan'],
      'Crossover/SUV':   ['crossover/suv', 'suv', 'crossover', 'sport utility'],
      'Truck':           ['truck', 'pickup'],
      'Coupe':           ['coupe'],
      'Convertible':     ['convertible'],
      'Hatchback':       ['hatchback'],
      'Van':             ['van', 'minivan'],
      'Wagon':           ['wagon'],
      'Hybrid/Electric': ['hybrid/electric', 'hybrid', 'electric'],
      '4WD/AWD':         ['4wd/awd', '4wd', 'awd', '4x4'],
    };

    this.vehicleTypeOptions = this.vehicleTypeOptions.map(option => {
      const keywords = bodyStyleMap[option.value] || [option.value.toLowerCase()];
      const count = Object.entries(bodyStyleCounts).reduce((sum, [key, val]) => {
        return sum + (keywords.some(kw => key.includes(kw)) ? val : 0);
      }, 0);
      return { ...option, count };
    });

    // Update category tabs: All = totalAvailable, Available = same, others = 0
    // (API only returns non-sold-out vehicles)
    this.categoryTabs = this.categoryTabs.map(tab => {
      if (tab.value === 'all' || tab.value === 'available') {
        return { ...tab, count: totalAvailable };
      }
      return { ...tab, count: 0 };
    });
  },

  // Update vehicle type counts based on current inventory
  updateVehicleTypeCounts() {
    // If API already set accurate body_style_counts, skip re-computing from partial batch
    if (Object.keys(this.apiBodyStyleCounts).length > 0) return;

    if (!this.reps_users || this.reps_users.length === 0) {
      // Reset counts to 0 if no vehicles
      this.vehicleTypeOptions = this.vehicleTypeOptions.map(option => ({
        ...option,
        count: 0
      }));
      return;
    }

    const typeCounts = {
      SUV: 0,
      Sedan: 0,
      Truck: 0,
      Coupe: 0,
      Van: 0,
      Other: 0
    };

    this.reps_users.forEach(vehicle => {
      const bodyType = (vehicle.body_type || vehicle.bodyStyle || vehicle.body_style || '').toLowerCase();

      // Map various body type names to our standard options
      if (bodyType.includes('suv') || bodyType.includes('crossover') || bodyType.includes('sport utility')) {
        typeCounts.SUV++;
      } else if (bodyType.includes('sedan') || bodyType.includes('4dr') || bodyType.includes('four door')) {
        typeCounts.Sedan++;
      } else if (bodyType.includes('truck') || bodyType.includes('pickup') || bodyType.includes('crew cab')) {
        typeCounts.Truck++;
      } else if (bodyType.includes('coupe') || bodyType.includes('2dr') || bodyType.includes('two door')) {
        typeCounts.Coupe++;
      } else if (bodyType.includes('van') || bodyType.includes('minivan')) {
        typeCounts.Van++;
      } else if (bodyType) {
        typeCounts.Other++;
      }
    });

    console.log('📊 Vehicle type counts:', typeCounts);

    // Update the counts in vehicleTypeOptions
    this.vehicleTypeOptions = this.vehicleTypeOptions.map(option => ({
      ...option,
      count: typeCounts[option.value] || 0
    }));
  },

  // Set active category tab
  setActiveCategory(category) {
    this.activeCategory = category;
    console.log('📂 Category changed to:', category);
    // The filteredUsers computed property will automatically filter based on activeCategory
    // Update counts for display
    this.updateCategoryCounts();
  },

  // Update category counts based on current inventory
  updateCategoryCounts() {
    // Use the API total when available (accurate across all pages, not just loaded batch)
    const total = this.count_vehicle || (this.reps_users ? this.reps_users.length : 0);
    if (!total) return;

    // The getAllVehicles API filters is_sold_out=false, so all returned vehicles are available.
    // Pending/Sold/Gettable don't apply to this data source.
    const counts = {
      all: total,
      available: total,
      pending: 0,
      sold: 0,
      gettable: 0
    };

    this.categoryTabs = this.categoryTabs.map(tab => ({
      ...tab,
      count: counts[tab.value] !== undefined ? counts[tab.value] : 0
    }));
  },

  // Change sort order - call API like Inventory.vue
  changeSortOrder(sort) {
    console.log('🔄 Changing sort order to:', sort);
    this.currentSort = sort;

    // Call API with new sort order (like Inventory.vue does)
    const domain = this.getSubdomain();
    if (domain) {
      this.getInventory(domain);
    }
  },

  // Load make options from vehicles
  loadMakeOptions() {
    if (this.reps_users && this.reps_users.length > 0) {
      const makes = [...new Set(this.reps_users
        .map(vehicle => vehicle.make)
        .filter(make => make)
        .sort()
      )];
      this.makeOptions = makes;
    }
  },

  // Calculate estimated monthly payment
  calculateEstimatedPayment(vehiclePrice, downPayment = 0, tradeIn = 0, creditScore = 641, termMonths = 60) {
    try {
      // Default interest rates based on credit score
      let interestRate = 8.5; // Default rate

      if (creditScore < 500) interestRate = 18.0;
      else if (creditScore <= 600) interestRate = 15.0;
      else if (creditScore <= 660) interestRate = 12.0;
      else if (creditScore <= 780) interestRate = 8.5;
      else if (creditScore <= 850) interestRate = 5.5;

      // Calculate loan amount
      const loanAmount = vehiclePrice - downPayment - tradeIn;

      if (loanAmount <= 0) return 0;

      // Calculate monthly payment using loan formula
      const monthlyRate = interestRate / 100 / 12;
      const monthlyPayment = loanAmount * (monthlyRate * Math.pow(1 + monthlyRate, termMonths)) /
                            (Math.pow(1 + monthlyRate, termMonths) - 1);

      return Math.round(monthlyPayment);
    } catch (error) {
      console.error('Error calculating payment:', error);
      return 0;
    }
  },

  // Get estimated payment for display
  getEstimatedPayment(vehicle) {
    if (!vehicle || (!vehicle.price && !vehicle.amount)) return null;

    const price = parseFloat(vehicle.price || vehicle.amount) || 0;
    if (price <= 0) return null;

    // Use default values for estimation
    const downPayment = Math.round(price * 0.1); // 10% down payment
    const estimatedPayment = this.calculateEstimatedPayment(price, downPayment);

    return {
      monthly: estimatedPayment,
      downPayment: downPayment,
      totalPrice: price
    };
  },

  // Note: Client-side sorting removed - now using server-side sorting via API like Inventory.vue

  // Start loading animations
  startLoadingAnimations() {
    console.log('Starting loading animations...');

    // Cycle through loading messages
    // this.loadingTextInterval = setInterval(() => {
    //   this.loadingTextIndex = (this.loadingTextIndex + 1) % this.loadingMessages.length;
    //   this.currentLoadingText = this.loadingMessages[this.loadingTextIndex];
    //   console.log('Loading text changed to:', this.currentLoadingText);
    // }, 1500); // Change text every 1.5 seconds for faster testing

    // Animate loading dots
    this.loadingDotInterval = setInterval(() => {
      this.loadingDotIndex = (this.loadingDotIndex % 3) + 1;
    }, 800); // Change dots every 0.8 seconds
  },

  // Stop loading animations
  stopLoadingAnimations() {
    if (this.loadingTextInterval) {
      clearInterval(this.loadingTextInterval);
      this.loadingTextInterval = null;
    }
    if (this.loadingDotInterval) {
      clearInterval(this.loadingDotInterval);
      this.loadingDotInterval = null;
    }
  },

  // Handle vehicle viewed events for digital assistant tracking and navigation
  handleVehicleViewed(vehicleIdOrData) {
    console.log('Vehicle viewed:', vehicleIdOrData);

    // Handle both ID (from TrueCarVehicleCard) and full event data
    let vehicleId = vehicleIdOrData;
    let vehicleData = null;

    // If it's an object with vehicle_id, extract the ID
    if (typeof vehicleIdOrData === 'object' && vehicleIdOrData.vehicle_id) {
      vehicleId = vehicleIdOrData.vehicle_id;
      vehicleData = vehicleIdOrData.vehicle_data || vehicleIdOrData.vehicle;
    } else if (typeof vehicleIdOrData === 'string' || typeof vehicleIdOrData === 'number') {
      // If it's just an ID, find the vehicle data
      vehicleData = this.filteredUsers.find(user => user.id == vehicleIdOrData);
    }

    // Track with digital assistant if available
    if (this.$refs.digitalAssistant && vehicleData) {
      this.$refs.digitalAssistant.trackVehicleView(vehicleId, {
        year: vehicleData.year,
        make: vehicleData.make,
        model: vehicleData.model,
        price: vehicleData.amount || vehicleData.price,
        mileage: vehicleData.odometer || vehicleData.mileage,
        vin: vehicleData.vin,
        stock_number: vehicleData.stock_number
      });
    }

    // Navigate to vehicle detail page (same logic as handleVehicleDetailRequested)
    this.handleVehicleDetailRequested(vehicleData || vehicleId);

    // Also emit to parent component for backward compatibility
    this.$emit('vehicle-viewed', {
      vehicle_id: vehicleId,
      vehicle_data: vehicleData
    });
  },

  // Handle vehicle detail requests - navigate to detail page
  handleVehicleDetailRequested(vehicle) {
    console.log('Vehicle detail requested:', vehicle);

    // Find the vehicle data if only ID is passed
    let vehicleData = vehicle;
    if (typeof vehicle === 'string' || typeof vehicle === 'number') {
      vehicleData = this.filteredUsers.find(user => user.id === vehicle);
    }

    if (!vehicleData) {
      console.error('Vehicle not found for detail view');
      return;
    }

    // Handle external vehicles
    if (vehicleData.is_external && vehicleData.source_url) {
      console.log('Opening external vehicle link:', vehicleData.source_url);
      window.open(vehicleData.source_url, '_blank');
      return;
    }

    // For internal vehicles, navigate to detail page
    const vehicleId = vehicleData.id;
    console.log('Navigating to vehicle detail page:', vehicleId);

    // If in modal mode, emit to parent to handle
    if (this.isModalMode) {
      console.log('Modal mode detected, emitting vehicle-detail-requested event');
      this.$emit('vehicle-detail-requested', {
        vehicle_id: vehicleId,
        vehicle: vehicleData
      });
      return;
    }

    // For demo vehicles, persist data to sessionStorage so the detail page can load it
    if (String(vehicleId).startsWith('demo-')) {
      sessionStorage.setItem('demo_vehicle_' + vehicleId, JSON.stringify(vehicleData));
    }

    // Navigate to vehicle detail page
    this.$router.push('/view-inventory-details/' + vehicleId);
  },

  // Handle agreement requests in modal mode
  handleAgreementRequested() {
    console.log('Agreement requested in modal');

    // Emit to parent to handle agreement modal
    this.$emit('agreement-requested');
  },

  // Handle lead required event from TrueCarVehicleCard
  handleLeadRequired(vehicle) {
    console.log('Lead required for vehicle interest:', vehicle);

    // Show toast message directing user to submit their info
    if (this.$toast) {
      this.$toast.info('Please submit your contact information first to express interest in this vehicle.', {
        position: 'top-right',
        duration: 5000
      });
    }

    // Optionally redirect to home page to fill out lead form
    // Or show a modal here - for now just log it
    console.log('User needs to submit lead form before expressing interest');
  },

  // Handle global vehicle view events
  handleGlobalVehicleView(eventData) {
    console.log('Global vehicle view on inventory page:', eventData);

    if (this.$refs.digitalAssistant) {
      this.$refs.digitalAssistant.trackVehicleView(
        eventData.vehicle_id,
        eventData.vehicle_data
      );
    }
  },

  // Handle global vehicle detail view events
  handleGlobalVehicleDetailView(eventData) {
    console.log('Global vehicle detail view on inventory page:', eventData);

    if (this.$refs.digitalAssistant) {
      this.$refs.digitalAssistant.trackVehicleView(
        eventData.vehicle_id,
        eventData.vehicle_data
      );
    }
  },

  // Handle global vehicle search events
  handleGlobalVehicleSearch(eventData) {
    console.log('Global vehicle search on inventory page:', eventData);

    // Could be used to inform the AI about user search preferences
    if (this.$refs.digitalAssistant) {
      // Store search context for AI
      this.$refs.digitalAssistant.searchContext = eventData.search_params;
    }
  },

  // Hide loading modal when data is received
  hideLoadingModal() {
    console.log('Hiding loading modal... Current state:', this.showLoadingModal);
    this.showLoadingModal = false;
    this.stopLoadingAnimations();
    console.log('Loading modal hidden. New state:', this.showLoadingModal);

    // Force a re-render to ensure the overlay is properly removed
    this.$nextTick(() => {
      console.log('Next tick - showLoadingModal:', this.showLoadingModal);
    });

    // Additional debugging - check if overlay element exists
    setTimeout(() => {
      const overlayElements = document.querySelectorAll('.loading-overlay');
      console.log('Overlay elements found:', overlayElements.length);
      overlayElements.forEach((el, index) => {
        console.log(`Overlay ${index}:`, el.style.display, el.style.visibility);
      });
    }, 100);
  },


  prevSlide(index) {
  if(index !=0 )
    this.index = index - 1;
  else
    this.index = this.videos.length - 1;

},
setMetaTag(metaTags) {
    let headTag = document.getElementsByTagName('head')[0]
      for (const i in metaTags) {
        // First, delete the meta tags.
        let oldTag = document.querySelector(`meta[name="${metaTags[i].name}"]`)
        if (oldTag) {
          document.querySelector(`meta[name="${metaTags[i].name}"]`).remove()
        }
        // Second, insert the replacement meta tags.
        let newTag = document.createElement('meta')
        newTag.setAttribute('name', metaTags[i].name)
        newTag.setAttribute(metaTags[i].name, metaTags[i].content)
        headTag.appendChild(newTag)
      }
    },

nextSlide(index) {
  if(index != this.videos.length - 1 )
    this.index = index + 1
  else
    this.index = 0
},
gotopayemnt: function () {
    let self = this;
    self.$router.push('/card-payment/'+this.$route.params.id);
},
checkSocial(social){
  if(!this.company.social_flags)
    return true;
  let social_flags = JSON.parse(this.company.social_flags);
  if(social_flags[social] == 1)
    return true;
  return false;
},
getSubdomain() {
    const hostname = window.location.hostname;
    const domainParts = hostname.split('.');

    // Assuming the domain is of the form subdomain.example.com
    if (domainParts.length > 2) {
        return domainParts[0];
    } else {
        // Fallback for localhost / development:
        // 1. Check URL query params (?subdomain=xxx or ?id=xxx)
        // 2. Check localStorage for domain stored when visiting the Card page
        return this.$route.query.subdomain ||
               this.$route.query.id ||
               localStorage.getItem('card_domain') ||
               null;
    }
}
,

loadDemoVehicles() {
  // Set a demo company so the template renders
  this.company = {
    user_id: 0,
    first_name: 'Demo',
    last_name: 'Dealership',
    company_name: 'Herrera Luxury Auto Sales',
    phone_m: '(555) 000-0000',
    email: 'demo@autosyncx.com'
  };

  // 12 demo vehicles with real car images from Unsplash (free license)
  const img = (id) => [{ image: `https://images.unsplash.com/${id}?w=800&q=80` }];
  this.reps_users = [
    {
      id: 'demo-1', year: '2023', make: 'BMW', model: '3 Series', trim: '330i',
      odometer: 12400, transmission: 'Automatic', exterior_color: 'Alpine White',
      fuel_type: 'Gasoline', amount: 42500, body_type: 'Sedan', status: 'available',
      dealer_name: 'Herrera Luxury Auto Sales', city: 'Los Angeles', state: 'CA',
      images: img('photo-1558486137-0634dd211be0')
    },
    {
      id: 'demo-2', year: '2022', make: 'Mercedes-Benz', model: 'C300', trim: 'AMG Line',
      odometer: 18900, transmission: 'Automatic', exterior_color: 'Obsidian Black',
      fuel_type: 'Gasoline', amount: 39800, body_type: 'Sedan', status: 'available',
      dealer_name: 'Herrera Luxury Auto Sales', city: 'Beverly Hills', state: 'CA',
      images: img('photo-1616549934892-595cbeb38ef5')
    },
    {
      id: 'demo-3', year: '2023', make: 'Audi', model: 'Q5', trim: 'Premium Plus',
      odometer: 8700, transmission: 'Automatic', exterior_color: 'Glacier White',
      fuel_type: 'Gasoline', amount: 51200, body_type: 'SUV', status: 'available',
      dealer_name: 'Herrera Luxury Auto Sales', city: 'Santa Monica', state: 'CA',
      images: img('photo-1606222168307-dc98b8658715')
    },
    {
      id: 'demo-4', year: '2022', make: 'Tesla', model: 'Model 3', trim: 'Long Range',
      odometer: 22000, transmission: 'Automatic', exterior_color: 'Pearl White',
      fuel_type: 'Electric', amount: 38900, body_type: 'Sedan', status: 'available',
      dealer_name: 'Herrera Luxury Auto Sales', city: 'San Francisco', state: 'CA',
      images: img('photo-1667766752715-2c8180c2e558')
    },
    {
      id: 'demo-5', year: '2021', make: 'Ford', model: 'F-150', trim: 'XLT',
      odometer: 34500, transmission: 'Automatic', exterior_color: 'Race Red',
      fuel_type: 'Gasoline', amount: 34700, body_type: 'Truck', status: 'available',
      dealer_name: 'Herrera Luxury Auto Sales', city: 'Houston', state: 'TX',
      images: img('photo-1667766754912-a7e33162c8e4')
    },
    {
      id: 'demo-6', year: '2023', make: 'Toyota', model: 'Camry', trim: 'XSE',
      odometer: 5200, transmission: 'Automatic', exterior_color: 'Midnight Black',
      fuel_type: 'Gasoline', amount: 29500, body_type: 'Sedan', status: 'available',
      dealer_name: 'Herrera Luxury Auto Sales', city: 'Phoenix', state: 'AZ',
      images: img('photo-1763789381177-cd8a04aaa2ef')
    },
    {
      id: 'demo-7', year: '2022', make: 'Honda', model: 'CR-V', trim: 'Sport',
      odometer: 16800, transmission: 'CVT', exterior_color: 'Sonic Gray Pearl',
      fuel_type: 'Gasoline', amount: 31200, body_type: 'SUV', status: 'available',
      dealer_name: 'Herrera Luxury Auto Sales', city: 'Chicago', state: 'IL',
      images: img('photo-1763789381108-b5622140f2e0')
    },
    {
      id: 'demo-8', year: '2021', make: 'Chevrolet', model: 'Tahoe', trim: 'LT',
      odometer: 41200, transmission: 'Automatic', exterior_color: 'Iridescent Pearl',
      fuel_type: 'Gasoline', amount: 48600, body_type: 'SUV', status: 'available',
      dealer_name: 'Herrera Luxury Auto Sales', city: 'Dallas', state: 'TX',
      images: img('photo-1564422426545-8a43cd6598ff')
    },
    {
      id: 'demo-9', year: '2022', make: 'Lexus', model: 'RX 350', trim: 'F Sport',
      odometer: 21300, transmission: 'Automatic', exterior_color: 'Atomic Silver',
      fuel_type: 'Gasoline', amount: 55000, body_type: 'SUV', status: 'available',
      dealer_name: 'Herrera Luxury Auto Sales', city: 'Miami', state: 'FL',
      images: img('photo-1616549934892-595cbeb38ef5')
    },
    {
      id: 'demo-10', year: '2023', make: 'Porsche', model: 'Macan', trim: 'S',
      odometer: 4100, transmission: 'Automatic', exterior_color: 'Carrara White',
      fuel_type: 'Gasoline', amount: 72400, body_type: 'SUV', status: 'available',
      dealer_name: 'Herrera Luxury Auto Sales', city: 'Scottsdale', state: 'AZ',
      images: img('photo-1748214312050-4905dd659bc9')
    },
    {
      id: 'demo-11', year: '2022', make: 'RAM', model: '1500', trim: 'Laramie',
      odometer: 28700, transmission: 'Automatic', exterior_color: 'Diamond Black',
      fuel_type: 'Gasoline', amount: 46800, body_type: 'Truck', status: 'pending',
      dealer_name: 'Herrera Luxury Auto Sales', city: 'Denver', state: 'CO',
      images: img('photo-1558486137-0634dd211be0')
    },
    {
      id: 'demo-12', year: '2021', make: 'Cadillac', model: 'Escalade', trim: 'Premium Luxury',
      odometer: 38900, transmission: 'Automatic', exterior_color: 'Crystal White Tricoat',
      fuel_type: 'Gasoline', amount: 84900, body_type: 'SUV', status: 'available',
      dealer_name: 'Herrera Luxury Auto Sales', city: 'Las Vegas', state: 'NV',
      images: img('photo-1763789381177-cd8a04aaa2ef')
    },
    {
      id: 'demo-13', year: '2023', make: 'Mercedes-Benz', model: 'GLE 450', trim: 'AMG Line',
      odometer: 9800, transmission: 'Automatic', exterior_color: 'Selenite Grey',
      fuel_type: 'Gasoline', amount: 68500, body_type: 'SUV', status: 'available',
      dealer_name: 'Herrera Luxury Auto Sales', city: 'Beverly Hills', state: 'CA',
      images: img('photo-1606222168307-dc98b8658715')
    },
    {
      id: 'demo-14', year: '2022', make: 'BMW', model: 'X5', trim: 'xDrive40i',
      odometer: 19400, transmission: 'Automatic', exterior_color: 'Carbon Black',
      fuel_type: 'Gasoline', amount: 61900, body_type: 'SUV', status: 'available',
      dealer_name: 'Herrera Luxury Auto Sales', city: 'Scottsdale', state: 'AZ',
      images: img('photo-1558486137-0634dd211be0')
    },
    {
      id: 'demo-15', year: '2023', make: 'Audi', model: 'A7', trim: 'Prestige',
      odometer: 6200, transmission: 'Automatic', exterior_color: 'Navarra Blue',
      fuel_type: 'Gasoline', amount: 79800, body_type: 'Sedan', status: 'available',
      dealer_name: 'Herrera Luxury Auto Sales', city: 'Miami', state: 'FL',
      images: img('photo-1616549934892-595cbeb38ef5')
    }
  ];

  this.count_vehicle = this.reps_users.length;
  this.loader = false;
  this.reps_flag = true;
},

async loadAllVehicles() {
  // Set a minimal company object so the page template renders
  this.company = {
    user_id: 0,
    first_name: '',
    last_name: '',
    company_name: 'Herrera Luxury Auto Sales',
    phone_m: '',
    email: ''
  };

  // Reset lazy loading state for a fresh load
  this.lazyOffset = 0;
  this.hasMoreLazy = false;
  this.reps_users = [];
  this.loader = true;

  try {
    const response = await axios.get('/api/vehicles/all', {
      params: { limit: this.lazyPageSize, offset: 0 }
    });

    if (response.data.flag === 1) {
      this.reps_users = response.data.vehicles || [];
      this.count_vehicle = response.data.total || this.reps_users.length;
      this.hasMoreLazy = response.data.has_more || false;
      this.lazyOffset = this.reps_users.length;
      this.reps_flag = true;

      // Update sidebar counts from accurate API totals
      this.updateSidebarCountsFromApi(response.data);
    } else {
      console.error('Failed to load vehicles from API');
      this.reps_flag = true;
    }
  } catch (error) {
    console.error('Error loading vehicles:', error);
    this.reps_flag = true;
  } finally {
    this.loader = false;
    // Set up the scroll observer after the first batch is rendered
    this.$nextTick(() => this.setupLazyObserver());
  }

  // Also load filter options (makes, models, etc.)
  this.getInventoryFilter(null);
},

async loadMoreLazyVehicles() {
  if (this.isLoadingMore || !this.hasMoreLazy) return;

  this.isLoadingMore = true;
  try {
    const response = await axios.get('/api/vehicles/all', {
      params: { limit: this.lazyPageSize, offset: this.lazyOffset }
    });

    if (response.data.flag === 1) {
      const newVehicles = response.data.vehicles || [];
      this.reps_users = [...this.reps_users, ...newVehicles];
      this.lazyOffset += newVehicles.length;
      this.hasMoreLazy = response.data.has_more || false;
    }
  } catch (error) {
    console.error('Error loading more vehicles:', error);
  } finally {
    this.isLoadingMore = false;
  }
},

setupLazyObserver() {
  this.destroyLazyObserver();
  const sentinel = this.$refs.lazySentinel;
  if (!sentinel) return;

  this.lazyObserver = new IntersectionObserver((entries) => {
    if (entries[0].isIntersecting) {
      this.loadMoreLazyVehicles();
    }
  }, { rootMargin: '200px', threshold: 0 });

  this.lazyObserver.observe(sentinel);
},

destroyLazyObserver() {
  if (this.lazyObserver) {
    this.lazyObserver.disconnect();
    this.lazyObserver = null;
  }
},

 getInventory(id, customQuery = null){
  // Get query parameters from the URL or use provided custom query
  const query = customQuery || this.$route.query;

  // If we have URL parameters, restore the filter state
  if (Object.keys(query).length > 0) {
    this.restoreFilterState(query);
  }

  // Transform query parameters for API - convert comma-separated strings to arrays
  const transformedQuery = { ...query };

  // Convert makes from comma-separated string to array
  if (transformedQuery.makes && typeof transformedQuery.makes === 'string') {
    transformedQuery.makes = transformedQuery.makes.split(',').filter(make => make.trim() !== '');
  }

  // Convert models from comma-separated string to array
  if (transformedQuery.models && typeof transformedQuery.models === 'string') {
    transformedQuery.models = transformedQuery.models.split(',').filter(model => model.trim() !== '');
  }

  // Convert states from comma-separated string to array
  if (transformedQuery.states && typeof transformedQuery.states === 'string') {
    transformedQuery.states = transformedQuery.states.split(',').filter(state => state.trim() !== '');
  }

  // Convert zip_codes from comma-separated string to array
  if (transformedQuery.zip_codes && typeof transformedQuery.zip_codes === 'string') {
    transformedQuery.zip_codes = transformedQuery.zip_codes.split(',').filter(zip => zip.trim() !== '');
  }

  // Add sort parameter (like Inventory.vue does)
  if (!transformedQuery.sort && this.currentSort) {
    transformedQuery.sort = this.currentSort.value;
  }

  console.log('🔍 Original query:', query);
  console.log('🔍 Transformed query for API (with sort):', transformedQuery);

  // Create a data object to send to the API
  const data = {
    id: id,
    query: transformedQuery
  };

  // Make the API call
  axios({
    method: "POST",
    url: "/api/get-inventory-user",
    data: data
  })
  .then(async(response) => {
    console.log('🔍 API Response received:', response.data);
    console.log('🔍 Response flag:', response.data.flag);
    console.log('🔍 Response user count:', response.data.user && response.data.user.length || 0);

    if(response.data.flag == 1){
      this.reps_users = response.data.user;
      this.reps_flag = true;

      console.log('✅ Setting reps_users:', this.reps_users.length, 'vehicles');

      // Check if this is an AI search
      const urlParams = this.$route.query;
      const isAiSearch = urlParams.ai_search === '1';

      // For AI searches, keep loader false and manage the enhanced loading modal
      // For normal searches, turn off the loader to show content
      if (isAiSearch) {
        console.log('AI search: keeping loader false, managing enhanced modal');
        this.loader = false;
      } else {
        console.log('Normal search: turning off loader to show content');
        this.loader = false;
      }

      // Hide enhanced loading modal for AI searches when we have results from the main API
      // This ensures the modal is hidden whether we have 0 results or many results

      // Check if we should search external sources
      if (response.data.search_external) {
        console.log('🔍 Triggering external search...');
        // Add comparison mode to query if we're in comparison mode
        const searchQuery = { ...query };
        if (this.isComparisonMode) {
          searchQuery.comparison_mode = true;
        }
        // This will handle its own loading state
        this.searchExternalVehicles(id, searchQuery);
      } else {
        console.log('🔍 No external search needed - search_external flag:', response.data.search_external);
        console.log('🔍 Backend returned vehicles count:', this.reps_users.length);
        console.log('🔍 Backend vehicles:', this.reps_users);
        // this.hideLoadingModal();

        // If we have 0 results from backend, let's force external search for testing
        if (this.reps_users.length === 0) {
          console.log('🔍 No backend results found, forcing external search for testing...');
          const searchQuery = { ...query };
          if (this.isComparisonMode) {
            searchQuery.comparison_mode = true;
          }
          // this.searchExternalVehicles(id, searchQuery);
        }
      }
    } else {
      console.log('❌ API response flag is not 1, redirecting to login');
      // this.$router.push('/login');
    }
  })
  .catch(error => {
    console.error('Error fetching inventory:', error);
    this.loader = false;
    this.hideLoadingModal(); // Hide loading modal on error
  })
},

// Helper method to restore filter state from URL parameters
restoreFilterState(query) {
  try {
    console.log("Restoring filter state from query:", query);

    // Handle states first so we can use them for zip code determination
    const states = query.states ? query.states.split(',') : [];

    // Handle zip codes
    let zipCodes = [];
    if (query.zip_codes) {
      zipCodes = query.zip_codes.split(',');
    } else if (query.zip_code) {
      zipCodes = [query.zip_code];
    } else if (states.length > 0) {
      // If no zip codes and states are selected, use the states' zip codes
      zipCodes = states.map(state => this.stateZipMap[state] || '').filter(zip => zip !== '');
    }

    // For backward compatibility
    const zipCode = zipCodes.length > 0 ? zipCodes[0] : '';

    if (zipCodes.length > 0) {
      console.log(`Using zip codes: ${zipCodes.join(', ')}`);
    } else if (states.length > 0) {
      console.log(`No zip codes provided, using zip codes from states: ${zipCodes.join(', ')}`);
    }

    // Create a filter object from URL parameters
    const filterState = {
      // Handle backward compatibility - check both 'makes' and 'make'
      makes: query.makes ? query.makes.split(',') :
             (query.make ? [query.make] : []),

      // Handle backward compatibility - check both 'models' and 'model'
      models: query.models ? query.models.split(',') :
              (query.model ? [query.model] : []),

      // Use the states we already parsed
      states: states,

      // Use the zip codes we already parsed
      zip_codes: zipCodes,

      // Handle year - check both specific fields and backward compatibility
      year_min: query.year_min || query.year || '',
      year_max: query.year_max || '',

      // Price range
      price_min: query.price_min || '',
      price_max: query.price_max || '',

      // Odometer range
      odometer_min: query.odometer_min || '',
      odometer_max: query.odometer_max || '',

      // Location - use the determined zip code for backward compatibility
      zip_code: zipCode,
      radius: query.radius ? parseInt(query.radius) : 50,

      // Other parameters
      bodyStyle: query.body_style || '',

      // Add timestamp for persistence tracking
      timestamp: new Date().getTime()
    };

    console.log("Created filter state:", filterState);

    // Save to localStorage for the filter modal to use
    localStorage.setItem('vehicle_filter', JSON.stringify(filterState));

    // Update the component's filter state
    this.filter = {
      type: 'All',
      make: query.make || '',
      model: query.model || '',
      makes: filterState.makes || [],
      models: filterState.models || [],
      states: filterState.states || [],
      zip_codes: filterState.zip_codes || [],
      year_min: filterState.year_min || '',
      year_max: filterState.year_max || '',
      price_min: filterState.price_min || '',
      price_max: filterState.price_max || '',
      odometer_min: filterState.odometer_min || '',
      odometer_max: filterState.odometer_max || '',
      zip_code: zipCode,
      radius: filterState.radius || 50,
      bodyStyle: filterState.bodyStyle || ''
    };

    // If the filter modal exists, force it to reload saved filters
    if (this.$refs.filterModal) {
      this.$refs.filterModal.loadSavedFilters();
    }
  } catch (error) {
    console.error('Error restoring filter state:', error);
  }
},

// Search for vehicles from external sources
searchExternalVehicles(id, query) {
  console.log('🔍 searchExternalVehicles called with id:', id, 'query:', query);

  // Show loading state for external search
  this.searching_external = true;
  // Keep loader false so the page content is visible
  this.loader = false;

  // Check if this is comparison mode
  const isComparisonMode = query.comparison_mode === 'true' || query.comparison_mode === true;

  if (isComparisonMode) {
    console.log('🔍 Comparison mode detected, making simultaneous API calls');
    this.searchExternalVehiclesComparison(id, query);
    return;
  }

  console.log('🔍 Normal external search mode');

  // Transform query for external search API
  const transformedQuery = { ...query };

  // Convert makes from comma-separated string to array if needed
  if (transformedQuery.makes && typeof transformedQuery.makes === 'string') {
    transformedQuery.makes = transformedQuery.makes.split(',').filter(make => make.trim() !== '');
  }

  // Convert models from comma-separated string to array if needed
  if (transformedQuery.models && typeof transformedQuery.models === 'string') {
    transformedQuery.models = transformedQuery.models.split(',').filter(model => model.trim() !== '');
  }

  // Create a data object to send to the API
  const data = {
    zip: transformedQuery.zip_code || '',
    zip_code: transformedQuery.zip_code || '',
    zip_codes: transformedQuery.zip_codes || '',
    radius: transformedQuery.radius || 100,
    make: transformedQuery.make || '',
    makes: transformedQuery.makes || '',
    model: transformedQuery.model || '',
    models: transformedQuery.models || '',
    year: transformedQuery.year || '',
    min_price: transformedQuery.price_min || '',
    max_price: transformedQuery.price_max || '',
    user_id: this.company.user_id
  };

  // Add a slight delay to make the AI search feel more realistic
  setTimeout(() => {
    console.log('🔍 Making external search API call with data:', data);

    // Create a new AbortController to be able to cancel the fetch if needed
    const controller = new AbortController();
    const signal = controller.signal;

    // Set up the request for streaming
    const request = new Request(process.env.VUE_APP_API_URL+'api/search-external-vehicles', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
      },
      body: JSON.stringify(data),
      signal: signal
    });

    // Use streaming approach for real-time vehicle updates
    this.processStreamingResponse(request);
  }, 1000); // 1 second delay to make the AI search feel more realistic
},

// Process streaming response for real-time vehicle updates
async processStreamingResponse(request) {
  try {
    const response = await fetch(request);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let vehicleCount = 0;
    let insideVehiclesArray = false;
    let braceCount = 0;
    let currentVehicle = '';

    console.log('🔍 Starting to process streaming response...');

    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        console.log('🔍 Streaming completed');

        // Hide loading modal when streaming is complete (whether we found vehicles or not)
        console.log(`🔍 Streaming completed with ${vehicleCount} vehicles found`);
        // this.hideLoadingModal();

        break;
      }

      // Decode the chunk and add to buffer
      const chunk = decoder.decode(value, { stream: true });
      buffer += chunk;

      console.log('🔍 Received chunk:', chunk);
      console.log('🔍 Current buffer length:', buffer.length);

      // Try to extract vehicles from incomplete JSON streams
      // Handle cases like: {"source":"external","vehicles":[{"year":2008...
      if (buffer.includes('"vehicles":[') && buffer.includes('"year":')) {
        console.log('🔍 Detected incomplete streaming JSON with vehicles, attempting to extract...');

        try {
          // Find the start of the vehicles array
          const vehiclesStart = buffer.indexOf('"vehicles":[') + 12;
          const vehiclesData = buffer.substring(vehiclesStart);
          console.log('🔍 Vehicles data section:', vehiclesData);

          // Extract individual vehicle objects using regex
          const vehicleRegex = /\{[^{}]*"year"[^{}]*\}/g;
          const vehicleMatches = vehiclesData.match(vehicleRegex);

          if (vehicleMatches && vehicleMatches.length > 0) {
            console.log(`🔍 Found ${vehicleMatches.length} vehicle objects in incomplete stream`);

            vehicleMatches.forEach((vehicleJson, index) => {
              try {
                const vehicle = JSON.parse(vehicleJson);
                console.log(`🔍 Extracted vehicle ${index + 1}:`, vehicle);

                if (vehicle && (vehicle.id || vehicle.title || vehicle.make)) {
                  vehicleCount++;
                  console.log(`🔍 Stream extract - Received vehicle ${vehicleCount}: ${vehicle.make} ${vehicle.model}`);

                  // Hide loading modal immediately when first vehicle is found
                  if (vehicleCount === 1) {
                    console.log('🔍 First vehicle found via stream extraction, hiding loading modal');
                    this.hideLoadingModal();
                  }

                  // Process the vehicle immediately
                  this.processExternalVehicles([vehicle]);
                }
              } catch (parseError) {
                console.log(`🔍 Failed to parse extracted vehicle ${index + 1}:`, parseError.message);
              }
            });

            // If we successfully extracted vehicles, skip the complex streaming logic
            if (vehicleMatches.length > 0) {
              continue;
            }
          }
        } catch (e) {
          console.log('🔍 Stream extraction failed, falling back to streaming logic:', e.message);
        }
      }

      // Try to directly parse comma-prefixed vehicle JSON
      if (chunk.trim().startsWith(',{') && chunk.trim().includes('"year":')) {
        console.log('🔍 Detected comma-prefixed vehicle JSON, attempting direct parse...');
        try {
          const cleanChunk = chunk.trim().substring(1); // Remove leading comma
          console.log('🔍 Clean chunk:', cleanChunk);

          if (cleanChunk.startsWith('{') && cleanChunk.endsWith('}')) {
            const vehicle = JSON.parse(cleanChunk);
            console.log('🔍 Successfully parsed vehicle from chunk:', vehicle);

            if (vehicle && (vehicle.id || vehicle.title || vehicle.make)) {
              vehicleCount++;
              console.log(`🔍 Direct parse - Received vehicle ${vehicleCount}: ${vehicle.make} ${vehicle.model}`);

              // Hide loading modal immediately when first vehicle is found
              if (vehicleCount === 1) {
                console.log('🔍 First vehicle found via direct parse, hiding loading modal');
                this.hideLoadingModal();
              }

              // Process the vehicle immediately
              this.processExternalVehicles([vehicle]);
              continue; // Skip the rest of the streaming logic for this chunk
            }
          }
        } catch (e) {
          console.log('🔍 Direct parse failed, falling back to streaming logic:', e.message);
        }
      }

      // Process the buffer to find complete JSON objects
      if (!insideVehiclesArray) {
        // Look for the start of the vehicles array
        if (buffer.includes('"vehicles":[')) {
          insideVehiclesArray = true;
          const startIndex = buffer.indexOf('"vehicles":[') + 12;
          buffer = buffer.substring(startIndex);
          braceCount = 0;
          currentVehicle = '';
          console.log('🔍 Found vehicles array start, processing vehicles...');
        } else if (buffer.includes('{"year":') || buffer.includes(',{"year":')) {
          // Fallback: if we see vehicle data directly, assume we're inside the vehicles array
          insideVehiclesArray = true;
          braceCount = 0;
          currentVehicle = '';
          console.log('🔍 Detected vehicle data directly, assuming inside vehicles array...');

          // If buffer starts with comma, remove it
          if (buffer.trim().startsWith(',')) {
            buffer = buffer.trim().substring(1);
            console.log('🔍 Removed leading comma from buffer');
          }
        }
      } else {
        // We're inside the vehicles array, process character by character
        let processedLength = 0;

        for (let i = 0; i < buffer.length; i++) {
          const char = buffer[i];

          if (char === '{') {
            braceCount++;
            currentVehicle += char;
          } else if (char === '}') {
            braceCount--;
            currentVehicle += char;

            // If braces are balanced, we have a complete vehicle
            if (braceCount === 0 && currentVehicle.trim() !== '') {
              try {
                // Validate that we have a complete JSON object
                const trimmedVehicle = currentVehicle.trim();
                console.log('🔍 Attempting to parse vehicle JSON:', trimmedVehicle);

                if (trimmedVehicle.startsWith('{') && trimmedVehicle.endsWith('}')) {
                  const vehicle = JSON.parse(trimmedVehicle);
                  console.log('🔍 Parsed vehicle object:', vehicle);

                  if (vehicle && (vehicle.id || vehicle.title || vehicle.make)) {
                    vehicleCount++;
                    console.log(`🔍 Received vehicle ${vehicleCount}: ${vehicle.make} ${vehicle.model}`);

                    // Hide loading modal immediately when first vehicle is found
                    if (vehicleCount === 1) {
                      console.log('🔍 First vehicle found, hiding loading modal');
                      this.hideLoadingModal();
                    }

                    // Process the vehicle immediately
                    this.processExternalVehicles([vehicle]);
                  } else {
                    console.log('🔍 Vehicle object missing required fields:', vehicle);
                  }
                } else {
                  console.log('🔍 Invalid JSON format:', trimmedVehicle);
                }
              } catch (e) {
                console.error('Error parsing vehicle JSON:', e);
                console.error('Problematic JSON string:', currentVehicle);
                // Don't break the loop, just skip this malformed object
              }

              // Reset for next vehicle and mark this section as processed
              currentVehicle = '';
              processedLength = i + 1;
            }
          } else if (braceCount > 0) {
            currentVehicle += char;
          } else if (char === ',' && braceCount === 0) {
            // Skip commas between objects when not inside an object
            processedLength = i + 1;
          } else if (char.match(/\s/) && braceCount === 0) {
            // Skip whitespace between objects when not inside an object
            processedLength = i + 1;
          }
        }

        // Remove processed data from buffer, keep unprocessed data
        if (processedLength > 0) {
          buffer = buffer.substring(processedLength);
        }
      }
    }

    console.log(`🔍 Streaming completed. Total vehicles received: ${vehicleCount}`);

  } catch (error) {
    console.error('Error processing streaming response:', error);
  } finally {
    this.external_search_done = true;
    this.searching_external = false;
  }
},

// Search for vehicles from external sources in comparison mode with simultaneous API calls
searchExternalVehiclesComparison(id, query) {
  console.log('🔍 Starting comparison mode external search');

  // Clear existing vehicles for fresh comparison
  this.reps_users = [];

  // Transform query for external search API
  const transformedQuery = { ...query };

  // Get different makes/models for comparison
  let makes = [];
  let models = [];

  if (transformedQuery.makes && typeof transformedQuery.makes === 'string') {
    makes = transformedQuery.makes.split(',').filter(make => make.trim() !== '');
  } else if (Array.isArray(transformedQuery.makes)) {
    makes = transformedQuery.makes.filter(make => make.trim() !== '');
  }

  if (transformedQuery.models && typeof transformedQuery.models === 'string') {
    models = transformedQuery.models.split(',').filter(model => model.trim() !== '');
  } else if (Array.isArray(transformedQuery.models)) {
    models = transformedQuery.models.filter(model => model.trim() !== '');
  }

  // Create different search combinations for comparison (max 3)
  // Priority: Different makes for each column
  const searchCombinations = [];

  if (makes.length > 0) {
    // Strategy: Use different makes for each column to ensure variety
    for (let i = 0; i < Math.min(3, makes.length); i++) {
      const make = makes[i];
      let model = '';

      // Try to find a matching model for this make
      if (models.length > 0) {
        // Use the corresponding model if available, otherwise use the first model
        model = models[i] || models[0] || '';
      }

      searchCombinations.push({ make, model });
    }

    // If we have fewer makes than 3, add more combinations with different models
    if (searchCombinations.length < 3 && models.length > makes.length) {
      for (let i = makes.length; i < Math.min(3, models.length); i++) {
        // Use the first make with different models
        searchCombinations.push({
          make: makes[0] || '',
          model: models[i] || ''
        });
      }
    }
  } else if (models.length > 0) {
    // Use different models with empty make
    for (let i = 0; i < Math.min(3, models.length); i++) {
      searchCombinations.push({ make: '', model: models[i] });
    }
  } else {
    // Fallback to a single search
    searchCombinations.push({ make: '', model: '' });
  }

  // Ensure we have at least one search combination
  if (searchCombinations.length === 0) {
    searchCombinations.push({ make: '', model: '' });
  }

  console.log('🔍 Search combinations for comparison:', searchCombinations);

  // Create base data for API calls
  const baseData = {
    zip: transformedQuery.zip_code || '',
    zip_code: transformedQuery.zip_code || '',
    radius: transformedQuery.radius || 100,
    year: transformedQuery.year || '',
    min_price: transformedQuery.price_min || '',
    max_price: transformedQuery.price_max || '',
    user_id: this.company.user_id
  };

  // Make simultaneous API calls for each combination with real-time streaming
  let completedCalls = 0;
  const totalCalls = searchCombinations.length;

  searchCombinations.forEach((combination, index) => {
    const data = {
      ...baseData,
      make: combination.make,
      model: combination.model
    };

    console.log(`🔍 Making API call ${index + 1} for:`, combination);

    // Make each API call independently with streaming support
    const request = new Request(process.env.VUE_APP_API_URL + 'api/search-external-vehicles', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
      },
      body: JSON.stringify(data)
    });

    // Process streaming response for comparison mode
    this.processStreamingResponseComparison(request, combination, index, () => {
      // Track completion
      completedCalls++;
      if (completedCalls === totalCalls) {
        console.log('🔍 All comparison API calls completed');
        this.external_search_done = true;
        this.searching_external = false;
      }
    });
  });
},

// Process streaming response for comparison mode
async processStreamingResponseComparison(request, combination, columnIndex, onComplete) {
  try {
    const response = await fetch(request);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let vehicleCount = 0;
    let insideVehiclesArray = false;
    let braceCount = 0;
    let currentVehicle = '';

    console.log(`🔍 Starting to process streaming response for ${combination.make} ${combination.model}...`);

    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        console.log(`🔍 Streaming completed for ${combination.make} ${combination.model}`);

        // Hide loading modal when streaming is complete (whether we found vehicles or not)
        console.log(`🔍 Streaming completed for ${combination.make} ${combination.model} with ${vehicleCount} vehicles found`);
        this.hideLoadingModal();

        break;
      }

      // Decode the chunk and add to buffer
      const chunk = decoder.decode(value, { stream: true });
      buffer += chunk;

      console.log(`🔍 Received chunk for ${combination.make} ${combination.model}:`, chunk);

      // Process the buffer to find complete JSON objects
      if (!insideVehiclesArray) {
        // Look for the start of the vehicles array
        if (buffer.includes('"vehicles":[')) {
          insideVehiclesArray = true;
          const startIndex = buffer.indexOf('"vehicles":[') + 12;
          buffer = buffer.substring(startIndex);
          braceCount = 0;
          currentVehicle = '';
        }
      } else {
        // We're inside the vehicles array, process character by character
        let processedLength = 0;

        for (let i = 0; i < buffer.length; i++) {
          const char = buffer[i];

          if (char === '{') {
            braceCount++;
            currentVehicle += char;
          } else if (char === '}') {
            braceCount--;
            currentVehicle += char;

            // If braces are balanced, we have a complete vehicle
            if (braceCount === 0 && currentVehicle.trim() !== '') {
              try {
                // Validate that we have a complete JSON object
                const trimmedVehicle = currentVehicle.trim();
                if (trimmedVehicle.startsWith('{') && trimmedVehicle.endsWith('}')) {
                  const vehicle = JSON.parse(trimmedVehicle);

                  if (vehicle && (vehicle.id || vehicle.title || vehicle.make)) {
                    vehicleCount++;
                    console.log(`🔍 Received vehicle ${vehicleCount} for ${combination.make} ${combination.model}: ${vehicle.make} ${vehicle.model}`);

                    // Hide loading modal immediately when first vehicle is found
                    if (vehicleCount === 1) {
                      console.log(`🔍 First vehicle found for ${combination.make} ${combination.model}, hiding loading modal`);
                      this.hideLoadingModal();
                    }

                    // Add column information and source to the vehicle
                    const vehicleWithColumn = {
                      ...vehicle,
                      comparison_column: columnIndex, // Column index for grouping
                      comparison_source: `${combination.make} ${combination.model}`.trim() || 'External',
                      comparison_make: combination.make,
                      comparison_model: combination.model
                    };

                    // Process the vehicle immediately
                    this.processExternalVehiclesComparison([vehicleWithColumn], columnIndex);
                  }
                }
              } catch (e) {
                console.error('Error parsing vehicle JSON:', e);
                console.error('Problematic JSON string:', currentVehicle);
                // Don't break the loop, just skip this malformed object
              }

              // Reset for next vehicle and mark this section as processed
              currentVehicle = '';
              processedLength = i + 1;
            }
          } else if (braceCount > 0) {
            currentVehicle += char;
          } else if (char === ',' && braceCount === 0) {
            // Skip commas between objects when not inside an object
            processedLength = i + 1;
          } else if (char.match(/\s/) && braceCount === 0) {
            // Skip whitespace between objects when not inside an object
            processedLength = i + 1;
          }
        }

        // Remove processed data from buffer, keep unprocessed data
        if (processedLength > 0) {
          buffer = buffer.substring(processedLength);
        }
      }
    }

    console.log(`🔍 Streaming completed for ${combination.make} ${combination.model}. Total vehicles received: ${vehicleCount}`);

  } catch (error) {
    console.error(`Error processing streaming response for ${combination.make} ${combination.model}:`, error);
  } finally {
    // Call completion callback
    if (onComplete) {
      onComplete();
    }
  }
},

// Process external vehicles for comparison mode with real-time streaming
processExternalVehiclesComparison(vehicles, columnIndex) {
  if (!vehicles || !Array.isArray(vehicles)) return;

  console.log(`🔍 Processing ${vehicles.length} vehicles for column ${columnIndex}`);

  // Format external vehicles to match the structure of local inventory
  const externalVehicles = vehicles.map((vehicle, index) => {
    // Generate a unique ID if none exists
    const vehicleId = vehicle.id || `ext-${Date.now()}-${columnIndex}-${index}`;

    // Use the siteName directly from the API response if available
    const siteName = vehicle.siteName || (vehicle.source ? vehicle.source.replace('.com', '') : 'External');

    return {
      id: vehicleId,
      make: vehicle.make || 'Unknown',
      model: vehicle.model || 'Unknown',
      year: vehicle.year || 'N/A',
      amount: vehicle.price || 0,
      vin: vehicle.vin,
      stock_number: 'EXT-' + (vehicleId.toString().substring(0, 6) || index),
      exterior_color: vehicle.exterior_color || 'N/A',
      interior_color: vehicle.interior_color || 'N/A',
      images: [{ image: vehicle.image || require('@/assets/img/coming-soon.png') }],
      source: siteName,
      source_url: vehicle.url || '#',
      title: vehicle.title || `${vehicle.year || ''} ${vehicle.make || ''} ${vehicle.model || ''}`,
      description: vehicle.description || `Located in ${vehicle.location || 'N/A'}`,
      dealer_name: vehicle.dealer_name || vehicle.location || 'External Dealer',
      mileage: vehicle.mileage || 'N/A',
      location: vehicle.location || 'N/A',
      phone: vehicle.phone || 'N/A',
      is_external: true,
      site_name: siteName,
      // Comparison-specific properties
      comparison_column: columnIndex,
      comparison_source: vehicle.comparison_source,
      comparison_make: vehicle.comparison_make,
      comparison_model: vehicle.comparison_model
    };
  });

  // Add external vehicles to the results immediately
  if (externalVehicles.length > 0) {
    // Force reactivity by using Vue.set or creating a new array
    this.$set(this, 'reps_users', [...this.reps_users, ...externalVehicles]);

    // If this is the first batch of results, update the UI to show we have external results
    if (!this.hasExternalResults && externalVehicles.length > 0) {
      this.hasExternalResults = true;
    }

    console.log(`🔍 Added ${externalVehicles.length} vehicles to column ${columnIndex}. Total vehicles: ${this.reps_users.length}`);

    // Force component update
    this.$forceUpdate();
  }
},

// Helper method to split a string of JSON objects separated by commas
splitJsonObjects(jsonString) {
  if (!jsonString || typeof jsonString !== 'string') return [];

  // This is a simplified approach that may not work for all JSON structures
  // Especially those with nested objects containing commas
  const result = [];
  let bracketCount = 0;
  let currentObj = '';

  for (let i = 0; i < jsonString.length; i++) {
    const char = jsonString[i];

    if (char === '{') bracketCount++;
    if (char === '}') bracketCount--;

    currentObj += char;

    // If we've completed an object and we're at a comma or the end of the string
    if (bracketCount === 0 && currentObj.trim() !== '') {
      if (char === '}') {
        // We've completed an object
        result.push(currentObj);
        currentObj = '';
      } else if (char === ',' && i < jsonString.length - 1) {
        // Skip the comma
        currentObj = '';
      }
    }
  }

  return result;
},

// Helper method to process external vehicles and add them to the UI
processExternalVehicles(vehicles) {
  console.log('🔍 processExternalVehicles called with:', vehicles);

  if (!vehicles || !Array.isArray(vehicles)) {
    console.log('🔍 Invalid vehicles data:', vehicles);
    return;
  }

  // Format external vehicles to match the structure of local inventory
  const externalVehicles = vehicles.map((vehicle, index) => {
    console.log(`🔍 Processing vehicle ${index}:`, vehicle);

    // Generate a unique ID if none exists
    const vehicleId = vehicle.id || `ext-${Date.now()}-${index}`;

    // Use the siteName directly from the API response if available
    const siteName = vehicle.siteName || (vehicle.source ? vehicle.source.replace('.com', '') : 'External');

    const transformedVehicle = {
      id: vehicleId,
      make: vehicle.make || 'Unknown',
      model: vehicle.model || 'Unknown',
      year: vehicle.year || 'N/A',
      amount: vehicle.price || vehicle.amount || 0, // Handle both price and amount fields
      vin: vehicle.vin || vehicleId, // Use VIN if available, otherwise use ID
      stock_number: 'EXT-' + (vehicleId.toString().substring(0, 6) || index),
      exterior_color: vehicle.exterior_color || 'N/A',
      interior_color: vehicle.interior_color || 'N/A',
      images: [{ image: vehicle.image || require('@/assets/img/coming-soon.png') }],
      source: siteName,
      source_url: vehicle.url || '#',
      title: vehicle.title || `${vehicle.year || ''} ${vehicle.make || ''} ${vehicle.model || ''}`,
      description: vehicle.description || `Located in ${vehicle.location || 'N/A'}`,
      dealer_name: vehicle.dealer_name || vehicle.location || 'External Dealer',
      mileage: vehicle.mileage || 'N/A',
      location: vehicle.location || 'N/A',
      phone: vehicle.phone || 'N/A',
      is_external: true, // Flag to identify external vehicles
      site_name: siteName // Store the site name separately
    };

    console.log(`🔍 Transformed vehicle ${index}:`, transformedVehicle);
    return transformedVehicle;
  });

  console.log('🔍 Formatted external vehicles:', externalVehicles);

  // Add external vehicles to the results immediately with proper reactivity
  if (externalVehicles.length > 0) {
    console.log('🔍 Current reps_users before adding:', this.reps_users.length);
    console.log('🔍 Adding external vehicles:', externalVehicles);

    // Force reactivity by using Vue.set or creating a new array
    this.$set(this, 'reps_users', [...this.reps_users, ...externalVehicles]);

    console.log('🔍 Current reps_users after adding:', this.reps_users.length);
    console.log('🔍 Updated reps_users array:', this.reps_users);

    // If this is the first batch of results, update the UI to show we have external results
    if (!this.hasExternalResults && externalVehicles.length > 0) {
      this.hasExternalResults = true;
      console.log('🔍 Set hasExternalResults to true');
    }

    console.log(`🔍 Added ${externalVehicles.length} vehicles from normal external search. Total vehicles: ${this.reps_users.length}`);

    // Force component update to ensure immediate display
    this.$forceUpdate();
    console.log('🔍 Forced component update');
  } else {
    console.log('🔍 No external vehicles to add');
  }
},
async copyURL() {
  console.log(window.location);
      try {
        await navigator.clipboard.writeText(window.location);
        alert('URL Copied, Now you can share the URL');
      } catch($e) {
        alert('Cannot copy');
      }
    },
checkFormSignup(){
  let flag = 1;
      if (!this.email) {
        Vue.$toast.error('Email is required', {
            position: 'top-right'
        });
        this.$refs['email'].focus()
        flag = 0;
      }
      if (!this.password && flag == 1) {
        Vue.$toast.error('Password is required', {
            position: 'top-right'
        });
        this.$refs['password'].focus()
        flag = 0;
      }

      if (flag == 1) {
        localStorage.setItem('temp_email',this.email)
        localStorage.setItem('temp_pass',this.password)
        localStorage.setItem('email_verified',0)
        if(this.company)
          this.$router.push('/signup/'+this.url)
        else
          this.$router.push('/signup')
      }
},
getStyle(flag){
  let self = this;
  let color = this.vcard.color;
  let font = this.vcard.header_color;
  let color_code = '';
  const colorArray = [
  { name: 'Orange', code: '#FD7E14' },
  { name: 'Green', code: '#28A745' },
  { name: 'Purple', code: '#6F42C1' },
  { name: 'Red', code: '#DC3545' },
  { name: 'Pink', code: '#E83E8C' },
  { name: 'Blue', code: '#007BFF' },
  { name: 'Dark Blue', code: '#0033cc' },
  { name: 'Black', code: '#000000' },
  { name: 'Silver', code: '#C0C0C0' },
  { name: 'Hot Pink', code: '#FF69B4' },
  { name: 'Flat Black', code: '#2D3436' }
];
colorArray.forEach(element => {
  if(element.name == color){
    color_code = element.code;
    self.color = color_code;
  }
});
if(flag == 'footer'){
  return 'background-color:'+color_code;
}
if(flag == 'button'){
  return color_code;
}

},
forgotPass(){
    this.buttonFlag =true;
    this.isDark = false;
    axios({
          method: "POST",

          url: "/api/auth/forgot-password",
          data: {
            email: this.email,
          }
        })
          .then(response => {
                this.sendFlag = 1;
                Vue.$toast.success('Forgot Password Mail sent', {
                position: 'top-right'
                });
                this.buttonFlag =false;
                this.isDark = true;
                this.pageFlag = 'login';
          })
          .catch(err => {
            Vue.$toast.error(err, {
            position: 'top-right'
            });
          })
},
gotosignup: function () {
    let self = this;
    self.$router.push('/signup');
},gotoGoogle(){
      let self = this;
        this.$router.push('/redirect/google');
        location.reload();
  },
  gotoMs(){
      let self = this;
        this.$router.push('/redirect/microsoft');
        location.reload();
  },
  gotoYahoo(){
      let self = this;
        this.$router.push('/redirect/yahoo');
        location.reload();
  },
  callNumber(phoneNumber) {
      if (phoneNumber) {
        window.location.href = `tel:${phoneNumber}`;
      }
    },
    goToCard() {

      this.$router.push('/');

    },
    sendEmail(email) {
      window.location.href = `mailto:${email}`;
    },
    gotoLinks(link){
      if(!link)
      link = '#'
      const newTab = window.open('', '_blank');
      newTab.location.href = link;
    },
    getCompanyInfo(id){

  axios({
          method: "POST",

          url: "/api/get-card",
          data: {
            id: id
          }

        })
          .then(async(response) => {
            if(response.data.flag == 1){
              if(id == 'reps'){

                this.reps_users = response.data.user;
                this.reps_flag = true;

              }else{
                this.company = response.data.user;
                this.vcard = response.data.vcard[0];
                this.sales_agent_count = response.data.sales_agents
                this.ref_agent_count = response.data.ref_agents
                // this.background =   this.base_url_img+'vcard/'+this.company.background
                // this.company.items = JSON.parse(JSON.parse(this.company.items))
                if(this.company.video_background){
                  let video_back = this.company.video_background.split("/");
                  this.vimeo_id = video_back[0]
                  this.vimeo_account = video_back[1]
                }
                let url = process.env.VUE_APP_WASBI_URL.replace('/logo','');
                const{base64Image, mimeType} = await this.convertImageToBase64(this.base_url_img+this.company.avatar);
                this.base64Image = base64Image
                this.avatar_mimeType = mimeType
              }
            }else{
              // this.$router.push('/login');
            }
          })

},
    addContact() {
      const contact = {
        name: "John Doe",
        phone: "123-456-7890",
        email: "johndoe@example.com",
        address: "1234 Example St, Anytown, USA",
      };

      if ('contacts' in navigator && 'ContactsManager' in window) {
        navigator.contacts.select(
          ['name', 'email', 'tel', 'address'],
          { multiple: false }
        ).then((contacts) => {
          const newContact = new Contact({
            name: [contact.name],
            tel: [{ value: contact.phone }],
            email: [{ value: contact.email }],
            address: [{ value: contact.address }],
          });
          newContact.save();
        }).catch((error) => {
          console.error('Error adding contact:', error);
        });
      } else {
        alert(`Name: ${contact.name}\nPhone: ${contact.phone}\nEmail: ${contact.email}\nAddress: ${contact.address}`);
      }
    },
    openInGoogleMaps(address) {
      const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
      window.open(googleMapsUrl, '_blank');
    },
    getSettings() {
    let flag = 1;
    let url = process.env.VUE_APP_WASBI_URL
    var config = {headers: {'Content-Type': 'application/json','Cache-Control' : 'no-cache'}};
      axios({
          method: "POST",
          url: "/api/get-company-settings",
          headers: {'Content-Type': 'application/json','Cache-Control' : 'no-cache, no-store, must-revalidate',Authorization:"Bearer " + localStorage.getItem('token')}
        })
        .then(response => {
            let data = response.data.data;
            if(data){
              this.company_settings = data.settings
               localStorage.setItem('settings', JSON.stringify(data.settings));
               if(data.settings.backgroung_logo)
                localStorage.setItem('admin_bg', JSON.stringify(url+data.settings.backgroung_logo));
              else
                localStorage.setItem('admin_bg','');
            }


         })
          .catch(err => {
            // Vue.$toast.error(err, {
            // position: 'top-right'
            // });

           });

     },
     checkPermission(flag) {

      if(flag == 'sales'){
        let status = 0;
        if(this.company_settings.sales_rep_flag == 'None'){
          return false
        }
        else if(this.company_settings.sales_rep_flag == 'All'){
          return true
        }else{
          if(this.company_settings.sales_rep_accounts.length != 0){
          this.company_settings.sales_rep_accounts.forEach(val => {
            if(val == this.company.user_id){
              status = 1;
            }

          });
        }
          return status
        }
      }
      if(flag == 'ref'){
        let status_ref = 0;
        if(this.company_settings.ref_rep_flag == 'None'){
          return false
        }
        else if(this.company_settings.ref_rep_flag == 'All'){
          return true
        }else{
          this.company_settings.ref_rep_accounts.forEach(val => {
            if(val == this.company.user_id){
              status_ref = 1;
            }

          });
          return status_ref
        }
      }
     },
  exportToFile(card, name='contact', force=true) {
            const a = document.createElement('a');
            const file = new Blob([card], { type: 'text/vcard'});

            a.href = URL.createObjectURL(file);
            a.download = name;
            a.click();

            URL.revokeObjectURL(a.href);
  },
  getIcon(id,value){
    if(id == 'Phone'){
      this.phone = value
      return 'fa fa-phone';
    }
    if(id == 'Zoom'){
      this.zoom_value = value
      return 'fas fa-video';
    }
    if(id == 'Event'){
      this.event_value = value
      return 'fas fa-calendar-check';
    }
    if(id == 'Email')
      return 'fas fa-envelope';
    if(id == 'Whatsapp')
      return 'fab fa-whatsapp';
    if(id == 'SMS/Text')
      return 'fas fa-comments';
    if(id == 'Website')
      return 'fas fa-link';
    if(id == 'Instagram')
      return 'fab fa-instagram';
    if(id == 'Snapchat')
      return 'fab fa-snapchat';
    if(id == 'Twitter')
      return 'fab fa-twitter';
    if(id == 'Facebook')
      return 'fab fa-facebook';
    if(id == 'LinkedIN')
      return 'fab fa-linkedin';
    else
      return 'fas fa-link';
  },
      // Function to convert image URL to Base64
  async convertImageToBase64(imageUrl) {
    let self = this;
    const response = await fetch(imageUrl);
    const blob = await response.blob();
    const mimeType = blob.type; // Get the MIME type of the image
    // this.avatar_mimeType = mimeType;
    // this.base64Image = blob
    // const reader = new FileReader();
    //     reader.onloadend = () => { self.base64Image = reader.result.split(',')[1]; self.avatar_mimeType = mimeType }; // Get Base64 string without the prefix
    //     reader.onerror = reject;
    //     reader.readAsDataURL(blob);
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve({ base64Image: reader.result.split(',')[1], mimeType }); // Get Base64 string without the prefix
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
},
getInventoryFilter(id){
  axios({
    method: "POST",
    url: "/api/get-vehicle-filters",
    data: {
      id: id
    }
  })
  .then(async(response) => {
    if(response.data.flag == 1){
      this.count_dealer = response.data.dealer;
      this.count_vehicle = response.data.count;
      this.count_make = response.data.make.length;
      this.make_option = response.data.make;
      this.model_option = response.data.model;

      // Get year options if available
      if (response.data.year) {
        this.year_option = response.data.year;
      } else {
        // Generate year options from current year down to 20 years ago
        const currentYear = new Date().getFullYear();
        this.year_option = [];
        for (let year = currentYear; year >= currentYear - 20; year--) {
          this.year_option.push(year.toString());
        }
      }

      // Set price range based on available inventory
      if (response.data.price_range) {
        this.price_range = {
          min: response.data.price_range.min || 0,
          max: response.data.price_range.max || 100000
        };
      }

      this.loader = false;
    } else {
      // Only redirect to login if a specific domain was requested
      // if (id) {
      //   this.$router.push('/login');
      // }
    }
  })
  .catch(error => {
    console.error('Error fetching vehicle filters:', error);
    this.loader = false;
  })
},

// Helper method to update URL with filter parameters
updateUrlWithFilters(query) {
  try {
    // Create a new URL object based on the current URL
    const url = new URL(window.location.href);

    // Clear existing query parameters
    url.search = '';

    // Define the order of parameters for better readability
    const paramOrder = [
      'make', 'model', 'makes', 'models',
      'year', 'year_min', 'year_max',
      'price_min', 'price_max',
      'odometer_min', 'odometer_max',
      'zip_code', 'radius',
      'states', 'body_style',
      'credit_score', 'bhph'
    ];

    // First add parameters in the defined order
    paramOrder.forEach(key => {
      // Include parameters even if they're empty strings
      if (key in query) {
        url.searchParams.set(key, query[key]);
      }
    });

    // Then add any remaining parameters not in the order list
    Object.keys(query).forEach(key => {
      if (!paramOrder.includes(key) && key in query) {
        url.searchParams.set(key, query[key]);
      }
    });

    // Update the URL without reloading the page
    window.history.pushState({}, '', url);

    console.log("Updated URL with filters:", url.toString());
  } catch (error) {
    console.error('Error updating URL with filters:', error);
  }
},

// Map of states to central zip codes
stateZipMap: {
  'AL': '35801', // Alabama - Huntsville
  'AK': '99501', // Alaska - Anchorage
  'AZ': '85001', // Arizona - Phoenix
  'AR': '72201', // Arkansas - Little Rock
  'CA': '95814', // California - Sacramento
  'CO': '80202', // Colorado - Denver
  'CT': '06103', // Connecticut - Hartford
  'DE': '19901', // Delaware - Dover
  'FL': '32301', // Florida - Tallahassee
  'GA': '30303', // Georgia - Atlanta
  'HI': '96813', // Hawaii - Honolulu
  'ID': '83702', // Idaho - Boise
  'IL': '62701', // Illinois - Springfield
  'IN': '46204', // Indiana - Indianapolis
  'IA': '50309', // Iowa - Des Moines
  'KS': '66603', // Kansas - Topeka
  'KY': '40601', // Kentucky - Frankfort
  'LA': '70802', // Louisiana - Baton Rouge
  'ME': '04330', // Maine - Augusta
  'MD': '21401', // Maryland - Annapolis
  'MA': '02201', // Massachusetts - Boston
  'MI': '48933', // Michigan - Lansing
  'MN': '55101', // Minnesota - St. Paul
  'MS': '39201', // Mississippi - Jackson
  'MO': '65101', // Missouri - Jefferson City
  'MT': '59601', // Montana - Helena
  'NE': '68502', // Nebraska - Lincoln
  'NV': '89701', // Nevada - Carson City
  'NH': '03301', // New Hampshire - Concord
  'NJ': '08608', // New Jersey - Trenton
  'NM': '87501', // New Mexico - Santa Fe
  'NY': '12207', // New York - Albany
  'NC': '27601', // North Carolina - Raleigh
  'ND': '58501', // North Dakota - Bismarck
  'OH': '43215', // Ohio - Columbus
  'OK': '73102', // Oklahoma - Oklahoma City
  'OR': '97301', // Oregon - Salem
  'PA': '17101', // Pennsylvania - Harrisburg
  'RI': '02903', // Rhode Island - Providence
  'SC': '29201', // South Carolina - Columbia
  'SD': '57501', // South Dakota - Pierre
  'TN': '37219', // Tennessee - Nashville
  'TX': '78701', // Texas - Austin
  'UT': '84111', // Utah - Salt Lake City
  'VT': '05602', // Vermont - Montpelier
  'VA': '23219', // Virginia - Richmond
  'WA': '98501', // Washington - Olympia
  'WV': '25301', // West Virginia - Charleston
  'WI': '53703', // Wisconsin - Madison
  'WY': '82001', // Wyoming - Cheyenne
  'DC': '20001'  // District of Columbia - Washington
},

handleFilterSearch(searchParams) {
  console.log("Filter search params:", searchParams); // Debug log

  // Get the current domain
  const domain = this.getSubdomain();

  // Check if we need to get a zip code from the selected state
  let zipCode = '';
  let zipCodes = [];

  // If zip_codes array is provided, use it
  if (searchParams.zip_codes && searchParams.zip_codes.length > 0) {
    zipCodes = [...searchParams.zip_codes];
    zipCode = zipCodes[0]; // Use first zip code for backward compatibility
    console.log(`Using zip codes from selection:`, zipCodes);
  }
  // Otherwise check for single zip_code
  else if (searchParams.zip_code) {
    zipCode = searchParams.zip_code;
    zipCodes = [zipCode];
    console.log(`Using single zip code: ${zipCode}`);
  }
  // If still no zip code and states are selected, use the states' zip codes
  else if (searchParams.states && searchParams.states.length > 0) {
    const states = Array.isArray(searchParams.states) ?
      searchParams.states :
      searchParams.states.split(',');

    zipCodes = states.map(state => this.stateZipMap[state] || '').filter(zip => zip !== '');
    zipCode = zipCodes.length > 0 ? zipCodes[0] : '';
    console.log(`No zip codes provided, using ${zipCodes.join(', ')} from selected states`);
  }

  // Create a query object from the filter parameters
  const query = {
    // For backward compatibility with the API
    make: searchParams.make ||
          (searchParams.makes && searchParams.makes.length > 0 ?
            searchParams.makes[0] : ''),

    model: searchParams.model ||
           (searchParams.models && searchParams.models.length > 0 ?
             searchParams.models[0] : ''),

    // Also include the full arrays for our URL and localStorage
    makes: searchParams.makes && searchParams.makes.length > 0 ?
      searchParams.makes.join(',') : '',
    models: searchParams.models && searchParams.models.length > 0 ?
      searchParams.models.join(',') : '',
    zip_codes: zipCodes.length > 0 ? zipCodes.join(',') : '',

    // Year range - include both specific fields and backward compatibility
    year: searchParams.year || searchParams.year_min || '',
    year_min: searchParams.year_min || '',
    year_max: searchParams.year_max || '',

    // Price range
    price_min: searchParams.price_min || '',
    price_max: searchParams.price_max || '',

    // Odometer range
    odometer_min: searchParams.odometer_min || '',
    odometer_max: searchParams.odometer_max || '',

    // Location - use the determined zip code for backward compatibility
    zip_code: zipCode,
    radius: searchParams.radius ? parseInt(searchParams.radius) : 50,

    // States
    states: searchParams.states && searchParams.states.length > 0 ?
      (Array.isArray(searchParams.states) ? searchParams.states.join(',') : searchParams.states) : '',

    // Other parameters
    body_style: searchParams.body_style || searchParams.bodyStyle || '',

    // Include required parameters for API
    credit_score: searchParams.credit_score || 641,
    bhph: searchParams.bhph || 0
  };

  console.log("Query object for URL:", query); // Debug log

  // Save the complete filter state to localStorage
  const filterState = {
    ...searchParams,
    zip_code: zipCode, // Save the determined zip code for backward compatibility
    zip_codes: zipCodes, // Save the zip codes array
    timestamp: new Date().getTime()
  };
  localStorage.setItem('vehicle_filter', JSON.stringify(filterState));

  // Update the filter with the search parameters
  // This needs to happen AFTER saving to localStorage to avoid circular references
  this.filter = {
    type: 'All',
    make: query.make || '',
    model: query.model || '',
    makes: searchParams.makes || [],
    models: searchParams.models || [],
    states: searchParams.states || [],
    zip_codes: zipCodes,
    year_min: query.year_min || '',
    year_max: query.year_max || '',
    price_min: query.price_min || '',
    price_max: query.price_max || '',
    odometer_min: query.odometer_min || '',
    odometer_max: query.odometer_max || '',
    zip_code: zipCode,
    radius: query.radius || 50,
    bodyStyle: query.body_style || ''
  };

  // Update URL with query parameters for bookmarking/sharing
  this.updateUrlWithFilters(query);

  // Call the inventory API with the new filter parameters
  this.getInventory(domain, query);
},

handleSyncGuideXSearch(searchParams) {
  console.log('SyncGuideX Search Params received:', searchParams);

  // Check if this is a comparison search
  if (searchParams.comparison_mode && searchParams.comparison_vehicles) {
    console.log('🔍 Detected comparison search, handling comparison mode...');
    this.handleComparisonSearch(searchParams);
    return;
  }

  // Clean up the search parameters - remove empty/null values
  const cleanedParams = {};
  Object.keys(searchParams).forEach(key => {
    if (searchParams[key] !== null && searchParams[key] !== undefined && searchParams[key] !== '') {
      cleanedParams[key] = searchParams[key];
    }
  });

  console.log('Cleaned SyncGuideX params:', cleanedParams);

  // Create URL parameters for navigation
  const urlParams = new URLSearchParams();

  // Add parameters to URL if they exist
  if (cleanedParams.make) urlParams.append('make', cleanedParams.make);
  if (cleanedParams.model) urlParams.append('model', cleanedParams.model);
  if (cleanedParams.year) urlParams.append('year', cleanedParams.year);
  if (cleanedParams.price_min) urlParams.append('price_min', cleanedParams.price_min);
  if (cleanedParams.price_max) urlParams.append('price_max', cleanedParams.price_max);
  if (cleanedParams.zip_code) urlParams.append('zip_code', cleanedParams.zip_code);
  if (cleanedParams.radius) urlParams.append('radius', cleanedParams.radius);
  if (cleanedParams.bodyStyle) urlParams.append('body_style', cleanedParams.bodyStyle);

  // Add credit score and bhph defaults
  urlParams.append('credit_score', '641');
  urlParams.append('bhph', '0');

  // Navigate to view-inventory with the search parameters
  const queryString = urlParams.toString();
  const url = queryString ? `/view-inventory?${queryString}` : '/view-inventory';

  console.log('Navigating to:', url);
  this.$router.push(url);
  location.reload();
},







/**
 * checkForm
 * login the user with valid email/pass
 */
async addCard(value) {
            const myVCard = new VCard()
            myVCard.addName(this.company.first_name+' '+this.company.last_name)
            if(this.company.business_name)
              myVCard.addCompany(this.company.business_name)
            myVCard.addEmail(this.company.email)
            myVCard.addPhoneNumber(this.company.phone_m)

            // Add social media links if available
            if (this.company.facebook) {
                myVCard.addSocial(this.company.facebook, 'Facebook');
            }
            if (this.company.instagram) {
                myVCard.addSocial(this.company.instagram, 'Instagram');
            }
            if (this.company.linkedin) {
                myVCard.addSocial(this.company.linkedin, 'TikTok');
            }
            if (this.company.website) {
                myVCard.addSocial(this.company.website, 'Website');
            }
            if (this.company.youtube) {
                myVCard.addSocial(this.company.youtube, 'YouTube');
            }
            if (this.company.avatar) {
              if (this.avatar_mimeType == 'image/png') {
                myVCard.addPhoto(`${this.base64Image}`, 'PNG');
              }
              else if (this.avatar_mimeType == 'image/jpeg') {
                myVCard.addPhoto(`${this.base64Image}`, 'JPEG');
              }
              else if (this.avatar_mimeType == 'image/gif') {
                myVCard.addPhoto(`${this.base64Image}`, 'GIF');
              }
            }
            const output = myVCard.toString()
            const preElement = document.querySelector('#output')
            const outputNode = document.createTextNode(output)
            this.exportToFile(output,'contact',true)

    },



    // Helper method to format price
    formatPrice(amount) {
      if (!amount) return '0';
      return parseFloat(amount).toLocaleString();
    },

    // Helper method to format mileage
    formatMileage(mileage) {
      if (!mileage || mileage === 'N/A') return 'N/A';
      return parseFloat(mileage).toLocaleString();
    },

    // Method to view vehicle details
    viewVehicleDetails(vehicle) {
      console.log('Viewing vehicle details:', vehicle);
      // You can implement navigation to vehicle detail page here
      // For now, just log the vehicle details
      alert(`Vehicle Details:\n${vehicle.year} ${vehicle.make} ${vehicle.model}\nPrice: $${this.formatPrice(vehicle.amount)}\nMileage: ${this.formatMileage(vehicle.mileage)}`);
    },

    handleComparisonSearch(searchParams) {
      console.log('🔍 Handling comparison search in CardInventory:', searchParams);

      // Build URL parameters for comparison search
      const urlParams = new URLSearchParams();

      // Add comparison flag
      urlParams.append('comparison_mode', 'true');

      // Add main vehicle parameters
      if (searchParams.main_vehicle) {
        if (searchParams.main_vehicle.make) urlParams.append('make', searchParams.main_vehicle.make);
        if (searchParams.main_vehicle.model) urlParams.append('model', searchParams.main_vehicle.model);
      }

      // Add comparison vehicles as makes and models
      if (searchParams.comparison_vehicles && Array.isArray(searchParams.comparison_vehicles)) {
        const allMakes = [searchParams.main_vehicle?.make].filter(Boolean);
        const allModels = [searchParams.main_vehicle?.model].filter(Boolean);

        searchParams.comparison_vehicles.forEach(vehicle => {
          if (vehicle.make && !allMakes.includes(vehicle.make)) {
            allMakes.push(vehicle.make);
          }
          if (vehicle.model && !allModels.includes(vehicle.model)) {
            allModels.push(vehicle.model);
          }
        });

        if (allMakes.length > 0) urlParams.append('makes', allMakes.join(','));
        if (allModels.length > 0) urlParams.append('models', allModels.join(','));
      }

      // Add other search parameters
      if (searchParams.price_min) urlParams.append('price_min', searchParams.price_min);
      if (searchParams.price_max) urlParams.append('price_max', searchParams.price_max);
      if (searchParams.zip_code) urlParams.append('zip_code', searchParams.zip_code);
      if (searchParams.radius) urlParams.append('radius', searchParams.radius);

      // Navigate to comparison view
      const currentPath = this.$route.path;
      const newUrl = `${currentPath}?${urlParams.toString()}`;

      console.log('🔍 Navigating to comparison URL:', newUrl);
      this.$router.push(newUrl);
    },


  }
};
</script>
<style>
/* Force Vuetify dark/light app root to be transparent so blurred ::before shows */
.v-application,
.theme--dark.v-application,
.theme--light.v-application,
.v-application--wrap {
  background: transparent !important;
  background-color: transparent !important;
}

  .check-login .v-label {
    color: white;
  }
  .check-login .v-icon {
    color: white;
  }
.input-login .v-icon{
    color: #FDE038!important;
}
.input-login .v-label{
  color: #FDE038!important;
}
.input-login #input-6{
  color: #FDE038!important;
}
.input-login #input-10{
  color: #FDE038!important;
}
.header-card{
  overflow: hidden;
  position: relative;
  z-index: 3;
  border-bottom: 1px solid rgb(217, 218, 220);
  color: rgb(20, 20, 20);
  background: linear-gradient(45deg, rgb(255, 117, 140), rgb(255, 126, 179));
}
.cards{
  box-sizing: border-box;
  position: relative;
  display: block;
  padding: 80px 10px 30px;
  background-color: rgb(230, 230, 230);
  border-bottom: 1px solid rgb(217, 218, 220);
  text-align: center;
  cursor: pointer;
  user-select: none;
  color: rgb(20, 20, 20);
}
.cards:nth-child(2n+1) {
  border-right: 1px solid rgb(217, 218, 220);
}
.cards:hover {
  color: skyblue!important;
  background: #D9E6F4;
}
.header {
  height: 80px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: -webkit-sticky;
  position: sticky;
  top: 0;
  border: solid #fff;
  border-width: 0 1px;
  z-index: 1;
  background: white;
  font-weight: 700;
}
.footer {
  height: 70px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: fixed;
  bottom: 0;
  border: 0px;
  border-width: 0 1px;
  z-index: 1;
  background: white;
  font-weight: 700;
  width: 100%;
}
.elementor-container {
text-align: center;
}
.elementor-column {
  position: relative;
  min-height: 1px;
  display: flex;
  justify-content: center;
}
.elementor-column-header {
  position: relative;
  min-height: 1px;
  display: flex;
  justify-content: center;
  margin-bottom: -170px;
  margin-top: 50px;
}
.elementor-heading-title-card{
  color: #FFFFFF;
  font-family: "Montserrat", Sans-serif;
  font-weight: 800;
  text-shadow: 4px 4px 10px rgba(0,0,0,0.3);
}
.elementor-icon-wrapper{
  font-size: 25px;
  color: #000000;
}
.elementor-element.elementor-element-09cffa4 img {
  width: 200px;
  height: 200px;
  border-style: solid;
  border-width: 5px 5px 5px 5px;
  border-color: #FFFFFF;
  border-radius: 300px 300px 300px 300px;

}
.elementor-element.elementor-element-a14cd74:not(.elementor-motion-effects-element-type-background), .elementor-581 .elementor-element.elementor-element-a14cd74 > .elementor-motion-effects-container > .elementor-motion-effects-layer {
  background: url("https://s3.us-central-1.wasabisys.com/quantumresolution/logo/david-bg.jpg") 50% 50%;
    background-size: auto;
  background-size: cover;
  padding: 60px 0px 70px 0px;
  position: relative;
}
.elementor-shape-bottom {
  bottom: -1px;
}
.elementor-shape {
  overflow: hidden;
  position: absolute;
  left: 0;
  width: 100%;
  line-height: 0;
  direction: ltr;
}
.elementor-shape .elementor-shape-fill {
  fill: #fff;
  transform-origin: center;
  transform: rotateY(0deg);
}
.elementor-heading-title2 {
  color: #303030;
  font-family: "Lora", Sans-serif;
  font-size: 32px;
  font-weight: 600;
  line-height: 40px;
}
 .elementor-social-icon {
  background-color: #021033;
}
.elementor-icon.elementor-social-icon {
  border-radius: 50%;
  font-size: 15px;
  line-height: 25px;
  width: 25px;
  display: inline-block;

  height: 25px;
}
.elementor-screen-only, .screen-reader-text, .screen-reader-text span, .ui-helper-hidden-accessible {
  position: absolute;
  top: -10000em;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0,0,0,0);
  border: 0;
}
.elementor-widget-container {
  text-align: center;
}
.elementor-social-icon i {
  color: #FFFFFF;
}
.elementor-grid-item{
  margin: 5px;
}
.slider {
  position: relative;
  width: 100%;
  height: 500px;
  overflow: hidden;
}

.slide {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  visibility: hidden;
  opacity: 0;
  transition: opacity 1s ease-in-out;
}

.slide.active {
  visibility: visible;
  opacity: 1;
}

iframe {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* AI Search Styling */
.ai-search-alert {
  border-left: 4px solid #1976d2 !important;
  background-color: rgba(25, 118, 210, 0.08) !important;
  margin-bottom: 16px !important;
}

.ai-results-alert {
  border-left: 4px solid #4caf50 !important;
  background-color: rgba(76, 175, 80, 0.08) !important;
  margin-bottom: 16px !important;
}

.ai-search-alert .v-progress-circular,
.ai-results-alert .v-icon {
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(25, 118, 210, 0.4);
  }

  70% {
    transform: scale(1);
    box-shadow: 0 0 0 10px rgba(25, 118, 210, 0);
  }

  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(25, 118, 210, 0);
  }
}

.loading-modal {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  width: 100%;
}

.loading-content {
  text-align: center;
  padding: 40px;
  background: white;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
  max-width: 500px;
  width: 90%;
  margin: 20px;
}

.loading-animation {
  margin-bottom: 30px;
}

.loading-text-container {
  margin-bottom: 30px;
}

.loading-text {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1976d2;
  margin-bottom: 15px;
  min-height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.loading-subtitle {
  font-size: 1rem;
  color: #666;
  margin: 0;
  line-height: 1.5;
}

.loading-dots {
  display: flex;
  justify-content: center;
  gap: 8px;
}

.dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: #e0e0e0;
  transition: all 0.3s ease;
}

.dot.active {
  background-color: #1976d2;
  transform: scale(1.2);
}

/* Fade transition for loading text */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

.container1 {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* AI Search Button Styling */
.ai-search-btn {
  font-weight: 600 !important;
  text-transform: none !important;
  border-radius: 8px !important;
  box-shadow: 0 4px 12px rgba(255, 152, 0, 0.3) !important;
  transition: all 0.3s ease !important;
  margin-top: 12px;
  margin-left: 5px;
}

.ai-search-btn:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 6px 20px rgba(255, 152, 0, 0.4) !important;
}

.ai-search-btn .v-icon {
  animation: sparkle 2s infinite;
}

@keyframes sparkle {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}
 .elementor-button {
  display: block;
  font-family: "Poppins", Sans-serif;
  font-size: 17px;
  font-weight: 400;
  text-transform: capitalize;
  fill: var( --e-global-color-6aef213 );
  color: var( --e-global-color-6aef213 );
  background-color: #FEFEFE;
  border-style: solid;
  border-width: 3px 3px 3px 3px;
  border-color: var( --e-global-color-6aef213 );
  border-radius: 50px 50px 50px 50px;
  box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.32);
  padding: 14px 50px 14px 50px;
}

.elementor-background-video-container {

    height: 100%;
    width: 100%;
    top: 0;
    left: 0;
    position: absolute;
    overflow: hidden;
    z-index: 0;
    direction: ltr;
    transition: opacity 1s;
    pointer-events: none;

}
 .elementor-background-video-hosted {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%,-50%);
}
</style>
